import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { HubAccessButton } from '@/components/settings/HubAccessButton';
import { HubOverrideRequestsPanel } from '@/components/settings/HubOverrideRequestsPanel';
import { useAuth } from '@/lib/auth';
import { useBiometricAuth } from '@/hooks/useBiometricAuth';
import api, { profileAPI, adminUsersAPI, teamsAPI, teamsV2API, usersAPI, atServiceSettingsAPI, payersAPI, statsAPI, parrainageAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Bell,
    BellOff,
    Trophy,
    UserPlus,
    XCircle,
    Clock,
    TrendingUp,
    Banknote,
    Users,
    Mail,
    Smartphone,
    Save,
    Building2,
    Loader2,
    User,
    Lock,
    Camera,
    Shield,
    UserCog,
    Snowflake,
    GraduationCap,
    Plus,
    Trash2,
    MapPin,
    Fingerprint,
    Eye,
    EyeOff,
    Zap,
    HardHat,
    Wallet,
    CreditCard,
    Pencil,
    Volume2,
    VolumeX,
    MessageSquare,
    AtSign,
    Heart,
    RefreshCw,
    Check,
    Wrench,
    AlertTriangle,
    Folder,
    Euro,
    Target,
    ChevronRight,
    ExternalLink,
    CheckCircle,
    X,
    Undo2,
    ArchiveRestore,
    ShieldCheck,
    Hash,
    BookOpen,
    Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import NotificationSettings from '@/components/communication/NotificationSettings';
import KnowledgeBaseAdmin from '@/components/settings/KnowledgeBaseAdmin';
import TenantAIConfigAdmin from '@/components/settings/TenantAIConfigAdmin';
import { EditUserDialog } from '@/components/admin/EditUserDialog';
import { DeletionRequestsPanel } from '@/components/admin/DeletionRequestsPanel';
import { ProspectsTrashPanel } from '@/components/admin/ProspectsTrashPanel';
import { FileVaultPanel } from '@/components/admin/FileVaultPanel';
import { HashtagManager } from '@/components/settings/HashtagManager';
import { ParrainSettingsTab } from '@/components/parrainage/ParrainSettingsTab';
import { resizeImageToDataURL } from '@/lib/imageResize';

const NOTIFICATION_TYPES = [
    { key: 'new_prospect', label: 'Nouveau prospect', icon: UserPlus, description: 'Quand un prospect est ajouté' },
    { key: 'prospect_signed', label: 'Contrat signé', icon: Trophy, description: 'Quand un contrat est finalisé' },
    { key: 'prospect_lost', label: 'Prospect perdu', icon: XCircle, description: 'Quand un prospect est perdu' },
    { key: 'prospect_stale', label: 'Prospect inactif', icon: Clock, description: 'Quand un prospect stagne' },
    { key: 'stage_advanced', label: 'Progression pipeline', icon: TrendingUp, description: 'Quand un prospect avance' },
    { key: 'commission_earned', label: 'Commission gagnée', icon: Banknote, description: 'Quand une commission est attribuée' },
    { key: 'team_member_joined', label: 'Nouveau membre', icon: Users, description: 'Quand quelqu\'un rejoint l\'équipe' },
];

const ROLE_NAMES = {
    admin_full: 'Admin Full Accès',
    admin: 'Admin',
    comptable: 'Comptable',
    vp: 'VP',
    apporteur: 'Apporteur',
    parrain: 'Parrain'
};

const ROLE_COLORS = {
    admin_full: 'bg-red-100 text-red-700',
    admin: 'bg-orange-100 text-orange-700',
    comptable: 'bg-blue-100 text-blue-700',
    vp: 'bg-purple-100 text-purple-700',
    apporteur: 'bg-green-100 text-green-700',
    parrain: 'bg-indigo-100 text-indigo-700'
};

const COMPANY_TYPES = [
    { id: 'ei', name: 'EI (Entreprise Individuelle)' },
    { id: 'eirl', name: 'EIRL' },
    { id: 'eurl', name: 'EURL' },
    { id: 'sarl', name: 'SARL' },
    { id: 'sas', name: 'SAS' },
    { id: 'sasu', name: 'SASU' },
    { id: 'sa', name: 'SA' },
    { id: 'snc', name: 'SNC' },
    { id: 'sci', name: 'SCI' },
    { id: 'auto', name: 'Auto-entrepreneur' },
];

// Biometric Settings Card Component
function BiometricSettingsCard() {
    const { user } = useAuth();
    const {
        isAvailable,
        isEnabled,
        isLoading,
        isMobile,
        enableBiometric,
        disableBiometric
    } = useBiometricAuth();
    
    const handleToggle = async () => {
        if (isEnabled) {
            disableBiometric();
            toast.success('Authentification biométrique désactivée');
        } else {
            const result = await enableBiometric(user?.id || 'user', user?.email || 'user@valnexo.com');
            if (result.success) {
                toast.success('Face ID / Touch ID activé !');
            } else {
                toast.error(result.error || 'Échec de l\'activation');
            }
        }
    };
    
    // Don't show on desktop
    if (!isMobile) {
        return null;
    }
    
    return (
        <Card className="mt-6">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Fingerprint className="w-5 h-5" />
                    Connexion biométrique
                </CardTitle>
                <CardDescription>
                    Connectez-vous avec Face ID ou Touch ID
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {isAvailable ? (
                    <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                                <Fingerprint className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <p className="font-medium text-slate-900">Face ID / Touch ID</p>
                                <p className="text-sm text-slate-500">
                                    {isEnabled ? 'Activé sur cet appareil' : 'Non configuré'}
                                </p>
                            </div>
                        </div>
                        <Switch
                            checked={isEnabled}
                            onCheckedChange={handleToggle}
                            disabled={isLoading}
                        />
                    </div>
                ) : (
                    <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                        <p className="text-sm text-amber-800">
                            L'authentification biométrique n'est pas disponible sur cet appareil.
                        </p>
                    </div>
                )}
                
                <p className="text-xs text-slate-500">
                    La connexion biométrique vous permet de vous reconnecter rapidement sans retaper votre mot de passe.
                    Vos données restent sécurisées sur votre appareil.
                </p>
            </CardContent>
        </Card>
    );
}

export default function Settings() {
    const { user, refreshUser, updateUser, logout } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'profile');
    // Deep-link doc KB (Uatu → clic sur badge KB dans le chat)
    const [kbDeepLinkDocId, setKbDeepLinkDocId] = useState(() => searchParams.get('doc') || null);

    // Sync du query param `tab` → state, et nettoyage du param `doc` après consommation
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (tabParam && tabParam !== activeTab) setActiveTab(tabParam);
        const docParam = searchParams.get('doc');
        if (docParam) {
            setKbDeepLinkDocId(docParam);
            // On retire `doc` de l'URL une fois lu — évite la ré-ouverture au refresh
            const next = new URLSearchParams(searchParams);
            next.delete('doc');
            setSearchParams(next, { replace: true });
        }
    }, [searchParams]);  // eslint-disable-line react-hooks/exhaustive-deps
    
    // Profile state
    const [profile, setProfile] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        profile_picture: null
    });
    const [passwordData, setPasswordData] = useState({
        current_password: '',
        new_password: '',
        confirm_password: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const fileInputRef = useRef(null);
    
    // Notifications state
    const [notificationSettings, setNotificationSettings] = useState({});
    
    // Company info state
    const [companyInfo, setCompanyInfo] = useState({
        company_type: '',
        company_name: '',
        siret: '',
        rcs: '',
        address: '',
        city: '',
        postal_code: '',
        phone: '',
        email: '',
        rib_iban: '',
        rib_bic: '',
        bank_name: ''
    });
    
    // Admin: Users management state
    const [allUsers, setAllUsers] = useState([]);
    const [showInactive, setShowInactive] = useState(false);
    const [createUserDialog, setCreateUserDialog] = useState(false);
    const [newUser, setNewUser] = useState({
        email: '',
        password: '',
        confirm_password: '',
        first_name: '',
        last_name: '',
        phone: '',
        role: 'apporteur',
        vp_id: '',
        parent_apporteur_id: '',
        direct_payer: null, // null = payé par VP, sinon ID du payeur (valnexo, at_service, ou custom)
        commission_rates: {
            easy_power: { value: '', is_active: true },
            chantier: { value: '', is_active: true }
        },
        // Champs Parrain (utilisés uniquement quand role === 'parrain')
        parrain_level: 1,
        parrain_branch: '',
        parrain_parent_id: '',
        commission_percent_ep_override: '',
        commission_percent_ch_override: '',
        // Mode d'invitation parrain : toujours 'password' — le créateur (VP/Admin) définit
        // le mot de passe et le parrain le reçoit par e-mail avec son identifiant.
        parrain_invite_mode: 'password',
    });
    // Données spécifiques au flow "créer un parrain" via ce dialog
    const [parrainageVps, setParrainageVps] = useState([]);              // VPs avec module Parrainage activé
    const [existingParrains, setExistingParrains] = useState([]);       // Liste des parrains du VP sélectionné (pour choisir le parent)
    const [globalParrainRates, setGlobalParrainRates] = useState({ ep: null, ch: null });
    const [inviteInfo, setInviteInfo] = useState(null);                 // Modal lien d'invitation après création
    const [showNewUserPassword, setShowNewUserPassword] = useState(false);
    const [availableParentApporteurs, setAvailableParentApporteurs] = useState([]);
    const [availablePayers, setAvailablePayers] = useState([]); // Liste des payeurs pour "Payé direct"
    const [freezeDialog, setFreezeDialog] = useState({ open: false, user: null });
    const [freezeData, setFreezeData] = useState({ reason: '', until: '', isPermanent: true });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
    const [deleteConfirm, setDeleteConfirm] = useState('');
    const [editUserDialog, setEditUserDialog] = useState({ open: false, user: null });
    const [editCommissionDialog, setEditCommissionDialog] = useState({ open: false, user: null });
    const [editCommissionData, setEditCommissionData] = useState({
        easy_power: { value: '' },
        chantier: { value: '' }
    });
    
    // Teams state
    const [teams, setTeams] = useState([]);
    const [createTeamDialog, setCreateTeamDialog] = useState(false);
    const [newTeam, setNewTeam] = useState({ name: '', description: '' });
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [addMemberDialog, setAddMemberDialog] = useState(false);
    const [newMember, setNewMember] = useState({ user_id: '', commission_rate: '' });
    const [teamHierarchy, setTeamHierarchy] = useState(null);
    
    // Valnexo Company Settings (Admin Full only)
    const [valnexoSettings, setValnexoSettings] = useState({
        company_name: '',
        address: '',
        postal_code: '',
        city: '',
        country: 'France',
        phone: '',
        email: '',
        website: '',
        siret: '',
        siren: '',
        tva_number: '',
        capital: '',
        rcs: '',
        iban: '',
        bic: '',
        bank_name: ''
    });
    
    // AT Service Settings (Admin Full only)
    const [atServiceSettings, setAtServiceSettings] = useState({
        company_name: 'AT Service',
        address: '',
        postal_code: '',
        city: '',
        country: 'France',
        phone: '',
        email: '',
        website: '',
        siret: '',
        siren: '',
        tva_number: '',
        capital: '',
        rcs: '',
        iban: '',
        bic: '',
        bank_name: '',
        at_service_max_share_percent: 30
    });
    
    // Payeurs personnalisés (Admin Full only)
    const [payers, setPayers] = useState([]);
    const [showPayerDialog, setShowPayerDialog] = useState(false);
    const [editingPayer, setEditingPayer] = useState(null);
    const [newPayer, setNewPayer] = useState({
        name: '',
        company_name: '',
        address: '',
        postal_code: '',
        city: '',
        country: 'France',
        phone: '',
        email: '',
        siret: '',
        siren: '',
        tva_number: '',
        iban: '',
        bic: '',
        bank_name: ''
    });
    
    // Dialog pour recréer les utilisateurs test
    
    // État pour la réparation des équipes (nouveau système)
    const [repairPreview, setRepairPreview] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [executingRepair, setExecutingRepair] = useState(false);
    const [repairResult, setRepairResult] = useState(null);
    
    // États pour le check de production
    const [productionCheckResult, setProductionCheckResult] = useState(null);
    const [loadingProductionCheck, setLoadingProductionCheck] = useState(false);
    const [recalculatingCommissions, setRecalculatingCommissions] = useState(false);
    
    // Audit des régressions d'étapes
    const [stageReversals, setStageReversals] = useState(null);
    const [loadingReversals, setLoadingReversals] = useState(false);
    
    // État pour le pilotage des membres
    const [memberDashboardOpen, setMemberDashboardOpen] = useState(false);
    const [selectedMemberDashboard, setSelectedMemberDashboard] = useState(null);
    const [loadingMemberDashboard, setLoadingMemberDashboard] = useState(false);
    const navigate = useNavigate();

    const canManageUsers = user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin === true || user?.role === 'vp';
    const canManageTeams = user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin === true || user?.role === 'vp';
    const isAdminFull = (user?.role === 'admin_full' || user?.is_admin === true);
    const isAdmin = (user?.role === 'admin' || user?.is_admin === true);
    const isVP = user?.role === 'vp';
    const isParrain = user?.role === 'parrain';

    useEffect(() => {
        loadSettings();
    }, []);

    useEffect(() => {
        if (canManageUsers) {
            loadUsers();
        }
    }, [showInactive]);

    const loadSettings = async () => {
        try {
            const requests = [
                api.get('/settings/notifications'),
                api.get('/company-info')
            ];
            
            // Charger les paramètres Valnexo, AT Service et Payeurs uniquement pour admin_full
            if ((user?.role === 'admin_full' || user?.is_admin === true)) {
                requests.push(api.get('/settings/company'));
                requests.push(api.get('/settings/at-service'));
                requests.push(api.get('/payers'));
            }
            
            const responses = await Promise.all(requests);
            
            const [notifRes, companyRes] = responses;
            setNotificationSettings(notifRes.data);
            if (companyRes.data) {
                setCompanyInfo(prev => ({ ...prev, ...companyRes.data }));
            }
            
            // Paramètres Valnexo
            if ((user?.role === 'admin_full' || user?.is_admin === true) && responses[2]) {
                setValnexoSettings(prev => ({ ...prev, ...responses[2].data }));
            }
            
            // Paramètres AT Service
            if ((user?.role === 'admin_full' || user?.is_admin === true) && responses[3]) {
                setAtServiceSettings(prev => ({ ...prev, ...responses[3].data }));
            }
            
            // Payeurs personnalisés
            if ((user?.role === 'admin_full' || user?.is_admin === true) && responses[4]) {
                setPayers(responses[4].data.filter(p => p.type === 'custom'));
            }
            
            if (user) {
                setProfile({
                    first_name: user.first_name || '',
                    last_name: user.last_name || '',
                    email: user.email || '',
                    phone: user.phone || '',
                    profile_picture: user.profile_picture || null
                });
            }
            
            if (canManageUsers) await loadUsers();
            if (canManageTeams) await loadTeams();
        } catch (error) {
            console.error('Error loading settings:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadUsers = async () => {
        try {
            const res = await adminUsersAPI.getAll(showInactive);
            let filteredUsers = res.data;
            
            // Si VP, filtrer pour ne voir que les membres de ses équipes
            if (isVP && user?.id) {
                const teamsRes = await teamsV2API.getAll();
                const myTeams = teamsRes.data.filter(t => 
                    t.payer_vp_id === user.id || t.supervisor_vp_id === user.id
                );
                
                // Récupérer tous les apporteurs de ces équipes (ceux qui ont vp_id = user.id)
                filteredUsers = res.data.filter(u => 
                    u.vp_id === user.id || u.id === user.id
                );
            }
            
            setAllUsers(filteredUsers);
        } catch (error) {
            console.error('Error loading users:', error);
        }
    };

    const loadTeams = async () => {
        try {
            // Utiliser teamsV2API pour charger depuis la collection teams_v2
            const res = await teamsV2API.getAll();
            setTeams(res.data);
        } catch (error) {
            console.error('Error loading teams:', error);
        }
    };

    const handleProfileChange = (e) => {
        const { name, value } = e.target;
        setProfile(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        // Validation du type
        if (!file.type.startsWith('image/')) {
            toast.error('Le fichier doit être une image (PNG, JPG, GIF, etc.)');
            return;
        }
        
        // Validation de la taille (5MB max)
        if (file.size > 5 * 1024 * 1024) {
            toast.error(`L'image fait ${(file.size / 1024 / 1024).toFixed(1)}MB. Maximum autorisé : 5MB`);
            return;
        }
        
        toast.info('Compression et upload en cours...');
        
        try {
            // Redimensionnement côté navigateur AVANT envoi (upload rapide même sur mobile)
            const resized = await resizeImageToDataURL(file, 512, 0.85);
            await profileAPI.uploadPicture(resized);
            setProfile(prev => ({ ...prev, profile_picture: resized }));
            // Rafraîchir les données utilisateur dans le contexte auth
            await refreshUser();
            toast.success('Photo mise à jour');
        } catch (error) {
            console.error('Upload error:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload de la photo');
        }
    };

    const saveProfile = async () => {
        setSaving(true);
        try {
            await profileAPI.update(profile);
            // Rafraîchir les données utilisateur dans le contexte auth
            await refreshUser();
            toast.success('Profil mis à jour');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        } finally {
            setSaving(false);
        }
    };

    const changePassword = async () => {
        if (passwordData.new_password !== passwordData.confirm_password) {
            toast.error('Les mots de passe ne correspondent pas');
            return;
        }
        if (passwordData.new_password.length < 6) {
            toast.error('Minimum 6 caractères');
            return;
        }
        setSaving(true);
        try {
            await profileAPI.changePassword({
                current_password: passwordData.current_password,
                new_password: passwordData.new_password
            });
            setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
            toast.success('Mot de passe modifié. Veuillez vous reconnecter.');
            // Force logout after password change for security
            setTimeout(() => {
                logout();
            }, 2000);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        } finally {
            setSaving(false);
        }
    };

    const handleNotificationChange = (key, value) => {
        setNotificationSettings(prev => ({ ...prev, [key]: value }));
    };

    const saveNotificationSettings = async () => {
        setSaving(true);
        try {
            await api.put('/settings/notifications', notificationSettings);
            toast.success('Notifications sauvegardées');
        } catch (error) {
            toast.error('Erreur');
        } finally {
            setSaving(false);
        }
    };

    const handleCompanyChange = (e) => {
        const { name, value } = e.target;
        setCompanyInfo(prev => ({ ...prev, [name]: value }));
    };

    const saveCompanyInfo = async () => {
        setSaving(true);
        try {
            await api.put('/company-info', companyInfo);
            toast.success('Société sauvegardée');
        } catch (error) {
            toast.error('Erreur');
        } finally {
            setSaving(false);
        }
    };
    
    const saveValnexoSettings = async () => {
        setSaving(true);
        try {
            await api.put('/settings/company', valnexoSettings);
            toast.success('Paramètres Valnexo sauvegardés - Les factures PDF utiliseront ces informations');
        } catch (error) {
            console.error('Error saving Valnexo settings:', error);
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };
    
    const handleValnexoChange = (e) => {
        const { name, value } = e.target;
        setValnexoSettings(prev => ({ ...prev, [name]: value }));
    };
    
    // AT Service functions
    const saveAtServiceSettings = async () => {
        setSaving(true);
        try {
            await api.put('/settings/at-service', atServiceSettings);
            toast.success('Paramètres AT Service sauvegardés');
        } catch (error) {
            console.error('Error saving AT Service settings:', error);
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };
    
    const handleAtServiceChange = (e) => {
        const { name, value, type } = e.target;
        setAtServiceSettings(prev => ({
            ...prev,
            [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value
        }));
    };
    
    // Payeurs personnalisés functions
    const handleCreatePayer = async () => {
        if (!newPayer.name || !newPayer.company_name) {
            toast.error('Le nom et la raison sociale sont obligatoires');
            return;
        }
        
        setSaving(true);
        try {
            if (editingPayer) {
                await payersAPI.update(editingPayer.id, newPayer);
                toast.success('Payeur modifié');
            } else {
                await payersAPI.create(newPayer);
                toast.success('Payeur créé');
            }
            
            // Recharger les payeurs
            const res = await payersAPI.getAll();
            setPayers(res.data.filter(p => p.type === 'custom'));
            
            setShowPayerDialog(false);
            setEditingPayer(null);
            setNewPayer({
                name: '',
                company_name: '',
                address: '',
                postal_code: '',
                city: '',
                country: 'France',
                phone: '',
                email: '',
                siret: '',
                siren: '',
                tva_number: '',
                iban: '',
                bic: '',
                bank_name: ''
            });
        } catch (error) {
            console.error('Error saving payer:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };
    
    const handleEditPayer = (payer) => {
        setEditingPayer(payer);
        setNewPayer({
            name: payer.name || '',
            company_name: payer.company_name || '',
            address: payer.address || '',
            postal_code: payer.postal_code || '',
            city: payer.city || '',
            country: payer.country || 'France',
            phone: payer.phone || '',
            email: payer.email || '',
            siret: payer.siret || '',
            siren: payer.siren || '',
            tva_number: payer.tva_number || '',
            iban: payer.iban || '',
            bic: payer.bic || '',
            bank_name: payer.bank_name || ''
        });
        setShowPayerDialog(true);
    };
    
    const handleDeletePayer = async (payerId) => {
        if (!confirm('Êtes-vous sûr de vouloir supprimer ce payeur ?')) return;
        
        try {
            await payersAPI.delete(payerId);
            toast.success('Payeur supprimé');
            setPayers(prev => prev.filter(p => p.id !== payerId));
        } catch (error) {
            console.error('Error deleting payer:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        }
    };
    
    // Ouvrir le dialog de création d'utilisateur et charger les payeurs + données parrainage
    const openCreateUserDialog = async () => {
        try {
            const res = await payersAPI.getAll();
            setAvailablePayers(res.data || []);
        } catch (error) {
            console.error('Error loading payers:', error);
        }
        // Précharge les VPs parrainage et taux globaux pour l'option "Parrain" (best-effort)
        try {
            const [vpsRes, settingsRes] = await Promise.all([
                parrainageAPI.listVps().catch(() => ({ data: [] })),
                parrainageAPI.getSettings().catch(() => ({ data: {} })),
            ]);
            setParrainageVps(vpsRes.data || []);
            const s = settingsRes.data || {};
            setGlobalParrainRates({ ep: s.easy_power?.direct ?? null, ch: s.chantier?.direct ?? null });
        } catch (e) { /* silencieux */ }
        setCreateUserDialog(true);
    };

    // Charge les parrains existants du VP sélectionné pour choisir un rattachement (N2/N3)
    const loadExistingParrainsForVp = async (vpId) => {
        if (!vpId) { setExistingParrains([]); return; }
        try {
            const res = await parrainageAPI.listParrains(vpId);
            setExistingParrains(res.data || []);
        } catch (e) { setExistingParrains([]); }
    };

    const handleCreateUser = async () => {
        // Branche PARRAIN : appel API dédié (parrainageAPI.createParrain), password optionnel
        if (newUser.role === 'parrain') {
            if (!newUser.first_name.trim() || !newUser.last_name.trim() || !newUser.email.trim()) {
                toast.error('Prénom, nom et email requis'); return;
            }
            const lvl = parseInt(newUser.parrain_level, 10);
            if (lvl === 1 && !newUser.parrain_branch.trim()) { toast.error('Nom de branche requis pour un Niveau 1'); return; }
            if (lvl > 1 && !newUser.parrain_parent_id) { toast.error('Parrain de rattachement requis'); return; }
            if (!isVP && !newUser.vp_id) { toast.error('VP responsable requis (Réseau Parrainage doit être activé sur ce VP)'); return; }
            // Le VP/Admin définit obligatoirement un mot de passe : le parrain le recevra
            // par e-mail avec son identifiant.
            if (!newUser.password || newUser.password.length < 6) { toast.error('Le mot de passe doit contenir au moins 6 caractères'); return; }
            if (newUser.password !== newUser.confirm_password) { toast.error('Les mots de passe ne correspondent pas'); return; }
            setSaving(true);
            try {
                const epOv = String(newUser.commission_percent_ep_override || '').trim();
                const chOv = String(newUser.commission_percent_ch_override || '').trim();
                const res = await parrainageAPI.createParrain({
                    first_name: newUser.first_name.trim(),
                    last_name: newUser.last_name.trim(),
                    email: newUser.email.trim().toLowerCase(),
                    phone: newUser.phone,
                    parrain_level: lvl,
                    parrain_branch: lvl === 1 ? newUser.parrain_branch.trim() : undefined,
                    parrain_parent_id: lvl > 1 ? newUser.parrain_parent_id : undefined,
                    parrain_vp_id: isVP ? user.id : newUser.vp_id,
                    commission_percent_ep_override: epOv === '' ? null : Number(epOv),
                    commission_percent_ch_override: chOv === '' ? null : Number(chOv),
                    password: newUser.password,
                });
                toast.success('Parrain créé — identifiants envoyés par e-mail');
                setCreateUserDialog(false);
                setNewUser(prev => ({
                    ...prev,
                    email: '', first_name: '', last_name: '', phone: '',
                    password: '', confirm_password: '',
                    parrain_branch: '', parrain_parent_id: '',
                    commission_percent_ep_override: '', commission_percent_ch_override: '',
                    parrain_invite_mode: 'password',
                }));
                await loadUsers();
            } catch (e) {
                toast.error(e.response?.data?.detail || 'Erreur lors de la création du parrain');
            } finally { setSaving(false); }
            return;
        }

        // Validation du mot de passe (autres rôles)
        if (!newUser.password || newUser.password.length < 6) {
            toast.error('Le mot de passe doit contenir au moins 6 caractères');
            return;
        }
        
        if (newUser.password !== newUser.confirm_password) {
            toast.error('Les mots de passe ne correspondent pas');
            return;
        }
        
        // Validation du barème obligatoire pour le VP UNIQUEMENT
        // (l'apporteur n'a plus de barème dans sa fiche : son % est géré sur la page Équipe)
        if (newUser.role === 'vp') {
            const epValue = parseFloat(newUser.commission_rates?.easy_power?.value);
            const chValue = parseFloat(newUser.commission_rates?.chantier?.value);
            
            if (isNaN(epValue) || isNaN(chValue)) {
                toast.error('Les barèmes de commission Courtage ET Travaux d’optimisation sont obligatoires');
                return;
            }
            if (epValue < 0 || epValue > 100 || chValue < 0 || chValue > 100) {
                toast.error('Le pourcentage doit être entre 0 et 100');
                return;
            }
        }
        
        setSaving(true);
        try {
            // Préparer les données avec les barèmes - trim email et password
            const userData = {
                ...newUser,
                email: newUser.email.trim().toLowerCase(),
                password: newUser.password,
                first_name: newUser.first_name.trim(),
                last_name: newUser.last_name.trim(),
                parent_apporteur_id: newUser.parent_apporteur_id || null,
                direct_payer: newUser.role === 'apporteur' ? (newUser.direct_payer || null) : null,
                commission_rates: (newUser.role === 'vp') ? {
                    easy_power: {
                        value: parseFloat(newUser.commission_rates.easy_power.value),
                        is_active: newUser.commission_rates.easy_power.is_active
                    },
                    chantier: {
                        value: parseFloat(newUser.commission_rates.chantier.value),
                        is_active: newUser.commission_rates.chantier.is_active
                    }
                } : null
            };
            
            // Si VP crée un apporteur, automatiquement le rattacher à lui-même
            if (isVP && newUser.role === 'apporteur') {
                userData.vp_id = user.id;
            }
            
            // Remove confirm_password before sending
            delete userData.confirm_password;
            
            console.log('Creating user with data:', { ...userData, password: '***' });
            await adminUsersAPI.create(userData);
            toast.success('Utilisateur créé');
            setCreateUserDialog(false);
            setShowNewUserPassword(false);
            setNewUser({ 
                email: '', password: '', confirm_password: '', first_name: '', last_name: '', phone: '', 
                role: 'apporteur', vp_id: '', parent_apporteur_id: '', direct_payer: null,
                commission_rates: {
                    easy_power: { value: '', is_active: true },
                    chantier: { value: '', is_active: true }
                }
            });
            setAvailableParentApporteurs([]);
            await loadUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        } finally {
            setSaving(false);
        }
    };

    const loadAvailableParentApporteurs = async (vpId) => {
        if (!vpId) {
            setAvailableParentApporteurs([]);
            return;
        }
        try {
            const res = await usersAPI.getApporteursForParent(vpId);
            setAvailableParentApporteurs(res.data || []);
        } catch (error) {
            console.error('Error loading parent apporteurs:', error);
            setAvailableParentApporteurs([]);
        }
    };

    const handleVpChange = async (vpId) => {
        setNewUser(prev => ({ ...prev, vp_id: vpId === "none" ? "" : vpId, parent_apporteur_id: '' }));
        if (vpId && vpId !== "none") {
            await loadAvailableParentApporteurs(vpId);
        } else {
            setAvailableParentApporteurs([]);
        }
    };

    const handleFreezeUser = async () => {
        if (!freezeDialog.user) return;
        setSaving(true);
        try {
            await adminUsersAPI.updateStatus(freezeDialog.user.id, {
                is_active: false,
                freeze_reason: freezeData.reason,
                freeze_until: freezeData.isPermanent ? null : (freezeData.until || null)
            });
            toast.success('Compte gelé');
            setFreezeDialog({ open: false, user: null });
            setFreezeData({ reason: '', until: '', isPermanent: true });
            await loadUsers();
        } catch (error) {
            toast.error('Erreur');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!deleteDialog.user) return;
        if (deleteConfirm !== deleteDialog.user.email) {
            toast.error("L'email ne correspond pas");
            return;
        }
        setSaving(true);
        try {
            const result = await api.delete(`/users/${deleteDialog.user.id}`);
            toast.success(result.data.message);
            setDeleteDialog({ open: false, user: null });
            setDeleteConfirm('');
            await loadUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        } finally {
            setSaving(false);
        }
    };

    const handleUnfreezeUser = async (userId) => {
        try {
            await adminUsersAPI.updateStatus(userId, { is_active: true });
            toast.success('Compte dégelé');
            await loadUsers();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const handleGrantAcademyAccess = async (userId) => {
        try {
            await api.post(`/admin/users/${userId}/grant-academy-access`);
            toast.success("Accès Académie réactivé. L'apporteur peut se former pour débloquer le CRM.");
            await loadUsers();
        } catch (error) {
            toast.error("Erreur lors de la réactivation de l'accès Académie");
        }
    };

    const handleToggleAuditor = async (u) => {
        try {
            const next = !u.is_auditor;
            await usersAPI.setAuditorAccess(u.id, next);
            toast.success(next ? "Rôle Auditeur attribué" : "Rôle Auditeur retiré");
            await loadUsers();
        } catch (error) {
            toast.error("Erreur lors de la modification du rôle Auditeur");
        }
    };

    const handleRoleChange = async (userId, newRole) => {
        try {
            await adminUsersAPI.updateRole(userId, newRole);
            toast.success('Rôle mis à jour');
            await loadUsers();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const openEditCommissionDialog = (userToEdit) => {
        const rates = userToEdit.commission_rates || {};
        setEditCommissionData({
            easy_power: { value: rates.easy_power?.value?.toString() || '' },
            chantier: { value: rates.chantier?.value?.toString() || '' }
        });
        setEditCommissionDialog({ open: true, user: userToEdit });
    };

    const handleUpdateCommissionRates = async () => {
        if (!editCommissionDialog.user) return;
        
        const userToEdit = editCommissionDialog.user;
        const epValue = parseFloat(editCommissionData.easy_power.value);
        const chValue = parseFloat(editCommissionData.chantier.value);
        
        if (isNaN(epValue) || isNaN(chValue)) {
            toast.error('Les deux barèmes sont obligatoires');
            return;
        }
        
        if (userToEdit.role === 'apporteur' && (epValue < 0 || epValue > 100 || chValue < 0 || chValue > 100)) {
            toast.error('Le pourcentage doit être entre 0 et 100');
            return;
        }
        
        setSaving(true);
        try {
            await adminUsersAPI.updateCommissionRates(userToEdit.id, {
                easy_power: { value: epValue },
                chantier: { value: chValue }
            });
            toast.success('Barèmes mis à jour');
            setEditCommissionDialog({ open: false, user: null });
            await loadUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        } finally {
            setSaving(false);
        }
    };

    const handleCreateTeam = async () => {
        setSaving(true);
        try {
            await teamsV2API.create(newTeam);
            toast.success('Équipe créée');
            setCreateTeamDialog(false);
            setNewTeam({ name: '', description: '' });
            await loadTeams();
        } catch (error) {
            toast.error('Erreur');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteTeam = async (teamId) => {
        if (!window.confirm('Supprimer cette équipe ?')) return;
        try {
            await teamsV2API.delete(teamId);
            toast.success('Équipe supprimée');
            setSelectedTeam(null);
            await loadTeams();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const loadTeamDetails = async (teamId) => {
        // Utiliser directement les données de teams qui contiennent déjà les membres enrichis
        const teamFromList = teams.find(t => t.id === teamId);
        if (teamFromList) {
            setSelectedTeam(teamFromList);
        } else {
            // Fallback sur l'API si pas trouvé dans la liste
            try {
                const res = await teamsV2API.getById(teamId);
                setSelectedTeam(res.data);
            } catch (error) {
                toast.error('Erreur');
            }
        }
    };

    const handleAddMember = async () => {
        if (!selectedTeam || !newMember.user_id) return;
        setSaving(true);
        try {
            await teamsV2API.addMember(selectedTeam.id, {
                user_id: newMember.user_id,
                commission_rate: newMember.commission_rate ? parseFloat(newMember.commission_rate) : null
            });
            toast.success('Membre ajouté');
            setAddMemberDialog(false);
            setNewMember({ user_id: '', commission_rate: '' });
            await loadTeamDetails(selectedTeam.id);
            await loadTeams();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveMember = async (userId) => {
        if (!selectedTeam) return;
        try {
            await teamsV2API.removeMember(selectedTeam.id, userId);
            toast.success('Membre retiré');
            await loadTeamDetails(selectedTeam.id);
            await loadTeams();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const handleUpdateMemberCommission = async (userId, rate) => {
        if (!selectedTeam) return;
        try {
            await teamsV2API.updateMemberCommission(selectedTeam.id, userId, { 
                commission_percent_ep: parseFloat(rate),
                commission_percent_ch: parseFloat(rate)
            });
            toast.success('Commission mise à jour');
            await loadTeamDetails(selectedTeam.id);
        } catch (error) {
            toast.error('Erreur');
        }
    };
    
    // Fonction pour ouvrir le dashboard de pilotage d'un membre
    const handleOpenMemberDashboard = async (memberId) => {
        setLoadingMemberDashboard(true);
        setMemberDashboardOpen(true);
        try {
            const response = await statsAPI.getMemberDashboard(memberId);
            setSelectedMemberDashboard(response.data);
        } catch (error) {
            console.error('Erreur chargement dashboard membre:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors du chargement');
            setMemberDashboardOpen(false);
        } finally {
            setLoadingMemberDashboard(false);
        }
    };
    
    // Formater un montant en euros
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount || 0);
    };
    
    // Labels des étapes de pipeline
    const getStageLabel = (stage) => {
        const stages = {
            // Courtage
            'ep_nouveau': 'Nouveau',
            'ep_qualification': 'Qualification',
            'ep_negociation': 'Négociation',
            'ep_proposition': 'Proposition',
            'ep_gagne': 'Gagné',
            // Travaux d’optimisation
            'ch_devis': 'Devis',
            'ch_negociation': 'Négociation',
            'ch_valide': 'Validé',
            'ch_paiement_effectue': 'Payé',
            // Autres
            'perdu': 'Perdu',
            'abandonne': 'Abandonné'
        };
        return stages[stage] || stage || '-';
    };
    
    // Couleur du badge selon l'étape
    const getStageBadgeColor = (stage) => {
        if (!stage) return 'bg-gray-100 text-gray-600';
        if (stage.includes('gagne') || stage.includes('valide') || stage.includes('paiement')) 
            return 'bg-green-100 text-green-700';
        if (stage.includes('perdu') || stage.includes('abandonne')) 
            return 'bg-red-100 text-red-700';
        if (stage.includes('negociation') || stage.includes('proposition')) 
            return 'bg-amber-100 text-amber-700';
        return 'bg-blue-100 text-blue-700';
    };

    const vps = allUsers.filter(u => (u.role === 'vp' || u.is_vp_effective) && u.is_active !== false);
    const teamMembers = selectedTeam?.members || [];
    const availableUsersForTeam = allUsers.filter(u => {
        if (!u.is_active) return false;
        const memberIds = selectedTeam?.members?.map(m => m.id) || [];
        return !memberIds.includes(u.id);
    });

    // Fonction pour charger l'aperçu des corrections
    const handleLoadRepairPreview = async () => {
        setLoadingPreview(true);
        setRepairPreview(null);
        setRepairResult(null);
        try {
            const response = await api.get('/admin/repair-teams/preview');
            setRepairPreview(response.data);
        } catch (error) {
            console.error('Erreur chargement aperçu:', error);
            toast.error('Erreur lors du chargement de l\'aperçu');
        } finally {
            setLoadingPreview(false);
        }
    };

    // Fonction pour exécuter la correction
    const handleExecuteRepair = async () => {
        if (!repairPreview || repairPreview.summary.auto_fixable === 0) {
            toast.info('Aucun dossier à corriger');
            return;
        }
        
        setExecutingRepair(true);
        try {
            const response = await api.post('/admin/repair-teams/execute');
            setRepairResult(response.data);
            toast.success(`${response.data.fixed_count} dossiers corrigés avec succès`);
            // Recharger l'aperçu
            await handleLoadRepairPreview();
        } catch (error) {
            console.error('Erreur correction:', error);
            toast.error('Erreur lors de la correction');
            setRepairResult({ error: error.message });
        } finally {
            setExecutingRepair(false);
        }
    };

    // ============== CHECK PRODUCTION ==============
    
    const handleRunProductionCheck = async () => {
        setLoadingProductionCheck(true);
        setProductionCheckResult(null);
        try {
            const response = await api.get('/production/check');
            setProductionCheckResult(response.data);
            if (response.data.status === 'PASS') {
                toast.success('Tous les tests de production passent !');
            } else {
                toast.warning('Certains tests ont échoué, vérifiez les détails');
            }
        } catch (error) {
            console.error('Erreur check production:', error);
            toast.error('Erreur lors de la vérification de production');
        } finally {
            setLoadingProductionCheck(false);
        }
    };

    const handleDeleteTestUser = async (userId, email) => {
        if (!window.confirm(`Êtes-vous sûr de vouloir désactiver l'utilisateur ${email} ?\n\nCette action est irréversible.`)) {
            return;
        }
        try {
            await api.delete(`/production/cleanup/users/${userId}`);
            toast.success(`Utilisateur ${email} désactivé`);
            // Refresh the check
            await handleRunProductionCheck();
        } catch (error) {
            console.error('Erreur suppression utilisateur:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la désactivation');
        }
    };

    const handleArchiveTestProspect = async (prospectId, name) => {
        if (!window.confirm(`Êtes-vous sûr de vouloir archiver le prospect "${name || prospectId}" ?\n\nCette action est irréversible.`)) {
            return;
        }
        try {
            await api.delete(`/production/cleanup/prospects/${prospectId}`);
            toast.success('Prospect archivé');
            // Refresh the check
            await handleRunProductionCheck();
        } catch (error) {
            console.error('Erreur archivage prospect:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'archivage');
        }
    };

    // Fonction pour charger l'audit des régressions d'étapes
    const handleLoadStageReversals = async () => {
        setLoadingReversals(true);
        try {
            const response = await api.get('/admin/audit/stage-reversals');
            setStageReversals(response.data);
        } catch (error) {
            console.error('Erreur chargement audit:', error);
            toast.error('Erreur lors du chargement de l\'audit');
        } finally {
            setLoadingReversals(false);
        }
    };

    // Formater une date ISO en format lisible
    const formatDate = (isoDate) => {
        if (!isoDate) return '-';
        const date = new Date(isoDate);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-96 w-full" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="max-w-5xl mx-auto space-y-6">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-outfit font-semibold text-slate-900">Paramètres</h1>
                    <p className="text-slate-600 mt-1">Configurez vos préférences</p>
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                    <TabsList className="flex-wrap h-auto gap-1 p-1">
                        <TabsTrigger value="profile" className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            <span className="hidden sm:inline">Profil</span>
                        </TabsTrigger>
                        <TabsTrigger value="notifications" className="flex items-center gap-2">
                            <Bell className="w-4 h-4" />
                            <span className="hidden sm:inline">Notifications</span>
                        </TabsTrigger>
                        {!isAdminFull && !isParrain && (
                            <TabsTrigger value="company" className="flex items-center gap-2">
                                <Building2 className="w-4 h-4" />
                                <span className="hidden sm:inline">Ma Société</span>
                            </TabsTrigger>
                        )}
                        {isParrain && (
                            <TabsTrigger value="parrain" className="flex items-center gap-2" data-testid="parrain-settings-tab">
                                <Building2 className="w-4 h-4" />
                                <span className="hidden sm:inline">Mes coordonnées</span>
                            </TabsTrigger>
                        )}
                        {isAdminFull && (
                            <TabsTrigger value="valnexo" className="flex items-center gap-2">
                                <Shield className="w-4 h-4" />
                                <span className="hidden sm:inline">Valnexo</span>
                            </TabsTrigger>
                        )}
                        {isAdminFull && (
                            <TabsTrigger value="at_service" className="flex items-center gap-2">
                                <Building2 className="w-4 h-4" />
                                <span className="hidden sm:inline">AT Service</span>
                            </TabsTrigger>
                        )}
                        {isAdminFull && (
                            <TabsTrigger value="payers" className="flex items-center gap-2">
                                <Wallet className="w-4 h-4" />
                                <span className="hidden sm:inline">Payeurs</span>
                            </TabsTrigger>
                        )}
                        {canManageUsers && (
                            <TabsTrigger value="users" className="flex items-center gap-2">
                                <UserCog className="w-4 h-4" />
                                <span className="hidden sm:inline">Utilisateurs</span>
                            </TabsTrigger>
                        )}
                        {canManageTeams && (
                            <TabsTrigger value="teams" className="flex items-center gap-2">
                                <Users className="w-4 h-4" />
                                <span className="hidden sm:inline">Équipes</span>
                            </TabsTrigger>
                        )}
                        {(isAdmin || isAdminFull) && (
                            <TabsTrigger value="hashtags" className="flex items-center gap-2">
                                <Hash className="w-4 h-4" />
                                <span className="hidden sm:inline">Hashtags</span>
                            </TabsTrigger>
                        )}
                        {isAdminFull && (
                            <TabsTrigger value="knowledge_base" className="flex items-center gap-2" data-testid="knowledge-base-tab">
                                <BookOpen className="w-4 h-4" />
                                <span className="hidden sm:inline">Base de connaissances</span>
                            </TabsTrigger>
                        )}
                        {isAdminFull && (
                            <TabsTrigger value="ai_assistant" className="flex items-center gap-2" data-testid="ai-assistant-tab">
                                <Sparkles className="w-4 h-4" />
                                <span className="hidden sm:inline">IA & Assistant</span>
                            </TabsTrigger>
                        )}
                        {isAdminFull && (
                            <TabsTrigger value="maintenance" className="flex items-center gap-2">
                                <Wrench className="w-4 h-4" />
                                <span className="hidden sm:inline">Maintenance</span>
                            </TabsTrigger>
                        )}
                    </TabsList>

                    {/* Profile Tab */}
                    <TabsContent value="profile" className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <User className="w-5 h-5" />
                                    Informations personnelles
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="flex items-center gap-6">
                                    <div 
                                        className="relative w-24 h-24 rounded-full bg-slate-100 flex items-center justify-center cursor-pointer hover:bg-slate-200 transition-colors overflow-hidden group border-2 border-dashed border-slate-300 hover:border-yellow-500"
                                        onClick={() => fileInputRef.current?.click()}
                                        data-testid="avatar-upload-trigger"
                                    >
                                        {profile.profile_picture ? (
                                            <img src={profile.profile_picture} alt="Profile" className="w-full h-full object-cover" />
                                        ) : (
                                            <span className="text-2xl font-semibold text-slate-500">
                                                {profile.first_name?.[0]}{profile.last_name?.[0]}
                                            </span>
                                        )}
                                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Camera className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <input 
                                        type="file" 
                                        ref={fileInputRef} 
                                        onChange={handleFileChange} 
                                        accept="image/png,image/jpeg,image/gif,image/webp" 
                                        className="hidden" 
                                        data-testid="avatar-file-input"
                                    />
                                    <div>
                                        <p className="font-medium">Photo de profil</p>
                                        <p className="text-sm text-slate-500">Cliquez pour modifier</p>
                                        <p className="text-xs text-slate-400">PNG, JPG, GIF • Max 5MB</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="first_name">Prénom</Label>
                                        <Input id="first_name" name="first_name" value={profile.first_name} onChange={handleProfileChange} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="last_name">Nom</Label>
                                        <Input id="last_name" name="last_name" value={profile.last_name} onChange={handleProfileChange} />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="email">Email</Label>
                                        <Input id="email" name="email" type="email" value={profile.email} onChange={handleProfileChange} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="phone">Téléphone</Label>
                                        <Input id="phone" name="phone" value={profile.phone} onChange={handleProfileChange} />
                                    </div>
                                </div>

                                <div className="flex justify-end pt-4">
                                    <Button onClick={saveProfile} className="bg-slate-900 hover:bg-slate-800" disabled={saving}>
                                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                                        Sauvegarder
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Lock className="w-5 h-5" />
                                    Modifier le mot de passe
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="current_password">Mot de passe actuel</Label>
                                    <div className="relative">
                                        <Input
                                            id="current_password"
                                            type={showPassword ? "text" : "password"}
                                            value={passwordData.current_password}
                                            onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                                        />
                                        <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowPassword(!showPassword)}>
                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="new_password">Nouveau mot de passe</Label>
                                        <Input id="new_password" type="password" value={passwordData.new_password} onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="confirm_password">Confirmer</Label>
                                        <Input id="confirm_password" type="password" value={passwordData.confirm_password} onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))} />
                                    </div>
                                </div>
                                <div className="flex justify-end pt-4">
                                    <Button onClick={changePassword} variant="outline" disabled={saving || !passwordData.current_password || !passwordData.new_password}>
                                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                                        Changer
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                        
                        {/* Biometric Authentication Card - Mobile Only */}
                        <BiometricSettingsCard />
                    </TabsContent>

                    {/* Notifications Tab */}
                    <TabsContent value="notifications">
                        <Card>
                            <CardHeader>
                                <CardTitle>Notifications</CardTitle>
                                <CardDescription>Choisissez les notifications à recevoir</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="space-y-4">
                                    {NOTIFICATION_TYPES.map((type) => (
                                        <div key={type.key} className="flex items-center justify-between p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm">
                                                    <type.icon className="w-5 h-5 text-slate-600" />
                                                </div>
                                                <div>
                                                    <Label className="font-medium">{type.label}</Label>
                                                    <p className="text-sm text-slate-500">{type.description}</p>
                                                </div>
                                            </div>
                                            <Switch checked={notificationSettings[type.key] !== false} onCheckedChange={(checked) => handleNotificationChange(type.key, checked)} />
                                        </div>
                                    ))}
                                </div>

                                <div className="pt-6 border-t border-slate-200">
                                    <h3 className="font-semibold text-slate-900 mb-4">Méthodes de livraison</h3>
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm">
                                                    <Smartphone className="w-5 h-5 text-slate-600" />
                                                </div>
                                                <div>
                                                    <Label className="font-medium">Notifications push</Label>
                                                    <p className="text-sm text-slate-500">Dans l'application</p>
                                                </div>
                                            </div>
                                            <Switch checked={notificationSettings.push_notifications !== false} onCheckedChange={(checked) => handleNotificationChange('push_notifications', checked)} />
                                        </div>
                                        <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm">
                                                    <Mail className="w-5 h-5 text-slate-600" />
                                                </div>
                                                <div>
                                                    <Label className="font-medium">Notifications par email</Label>
                                                    <p className="text-sm text-slate-500">Recevoir par email</p>
                                                </div>
                                            </div>
                                            <Switch checked={notificationSettings.email_notifications === true} onCheckedChange={(checked) => handleNotificationChange('email_notifications', checked)} />
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end pt-4">
                                    <Button onClick={saveNotificationSettings} className="bg-slate-900 hover:bg-slate-800" disabled={saving}>
                                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                                        Sauvegarder
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                        
                        {/* Notifications Push Navigateur */}
                        <div className="mt-6">
                            <NotificationSettings />
                        </div>
                    </TabsContent>

                    {/* Company Tab - Masqué pour Full Admin (ils utilisent Valnexo) et pour les Parrains (particuliers) */}
                    {!isAdminFull && !isParrain && (
                    <TabsContent value="company">
                        <Card>
                            <CardHeader>
                                <CardTitle>Informations Société</CardTitle>
                                <CardDescription>Pour vos factures</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Type de société</Label>
                                        <Select value={companyInfo.company_type} onValueChange={(v) => setCompanyInfo(prev => ({ ...prev, company_type: v }))}>
                                            <SelectTrigger><SelectValue placeholder="Sélectionnez..." /></SelectTrigger>
                                            <SelectContent>
                                                {COMPANY_TYPES.map((type) => (
                                                    <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="company_name">Désignation sociale *</Label>
                                        <Input id="company_name" name="company_name" value={companyInfo.company_name} onChange={handleCompanyChange} placeholder="Ma Société SARL" />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="siret">SIRET</Label>
                                        <Input id="siret" name="siret" value={companyInfo.siret} onChange={handleCompanyChange} placeholder="123 456 789 00012" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="rcs">RCS</Label>
                                        <Input id="rcs" name="rcs" value={companyInfo.rcs} onChange={handleCompanyChange} placeholder="Paris B 123 456 789" />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="address">Adresse</Label>
                                    <Input id="address" name="address" value={companyInfo.address} onChange={handleCompanyChange} placeholder="123 Rue de Paris" />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="postal_code">Code Postal</Label>
                                        <Input id="postal_code" name="postal_code" value={companyInfo.postal_code} onChange={handleCompanyChange} placeholder="75001" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="city">Ville</Label>
                                        <Input id="city" name="city" value={companyInfo.city} onChange={handleCompanyChange} placeholder="Paris" />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="company_phone">Téléphone</Label>
                                        <Input id="company_phone" name="phone" value={companyInfo.phone} onChange={handleCompanyChange} placeholder="+33 1 23 45 67 89" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="company_email">Email *</Label>
                                        <Input id="company_email" name="email" type="email" value={companyInfo.email} onChange={handleCompanyChange} placeholder="contact@societe.com" />
                                    </div>
                                </div>

                                <div className="pt-6 border-t border-slate-200">
                                    <h3 className="font-semibold text-slate-900 mb-4">Informations Bancaires (RIB)</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2 md:col-span-2">
                                            <Label htmlFor="rib_iban">IBAN</Label>
                                            <Input id="rib_iban" name="rib_iban" value={companyInfo.rib_iban} onChange={handleCompanyChange} placeholder="FR76 1234 5678 9012 3456 7890 123" />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="rib_bic">BIC</Label>
                                            <Input id="rib_bic" name="rib_bic" value={companyInfo.rib_bic} onChange={handleCompanyChange} placeholder="BNPAFRPP" />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="bank_name">Nom de la banque</Label>
                                            <Input id="bank_name" name="bank_name" value={companyInfo.bank_name} onChange={handleCompanyChange} placeholder="BNP Paribas" />
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end pt-4">
                                    <Button onClick={saveCompanyInfo} className="bg-slate-900 hover:bg-slate-800" disabled={saving}>
                                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                                        Sauvegarder
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                    )}

                    {/* Parrain : coordonnées personnelles + règlement + contrat */}
                    {isParrain && (
                        <TabsContent value="parrain">
                            <ParrainSettingsTab />
                        </TabsContent>
                    )}


                    {/* Valnexo Settings Tab (Admin Full only) */}
                    {isAdminFull && (
                        <TabsContent value="valnexo">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Shield className="w-5 h-5 text-yellow-600" />
                                        Paramètres Valnexo
                                    </CardTitle>
                                    <CardDescription>
                                        Ces informations apparaîtront sur les factures PDF générées
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    {/* Identité */}
                                    <div className="space-y-4">
                                        <h3 className="font-medium text-slate-700 flex items-center gap-2">
                                            <Building2 className="w-4 h-4" />
                                            Identité de l'entreprise
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="valnexo_company_name">Raison sociale *</Label>
                                                <Input
                                                    id="valnexo_company_name"
                                                    name="company_name"
                                                    value={valnexoSettings.company_name}
                                                    onChange={handleValnexoChange}
                                                    placeholder="Valnexo SAS"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="valnexo_siret">SIRET</Label>
                                                <Input
                                                    id="valnexo_siret"
                                                    name="siret"
                                                    value={valnexoSettings.siret}
                                                    onChange={handleValnexoChange}
                                                    placeholder="123 456 789 00012"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="valnexo_siren">SIREN</Label>
                                                <Input
                                                    id="valnexo_siren"
                                                    name="siren"
                                                    value={valnexoSettings.siren}
                                                    onChange={handleValnexoChange}
                                                    placeholder="123 456 789"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="valnexo_tva">N° TVA Intracommunautaire</Label>
                                                <Input
                                                    id="valnexo_tva"
                                                    name="tva_number"
                                                    value={valnexoSettings.tva_number}
                                                    onChange={handleValnexoChange}
                                                    placeholder="FR12345678901"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="valnexo_capital">Capital social</Label>
                                                <Input
                                                    id="valnexo_capital"
                                                    name="capital"
                                                    value={valnexoSettings.capital}
                                                    onChange={handleValnexoChange}
                                                    placeholder="10 000 €"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="valnexo_rcs">RCS</Label>
                                                <Input
                                                    id="valnexo_rcs"
                                                    name="rcs"
                                                    value={valnexoSettings.rcs}
                                                    onChange={handleValnexoChange}
                                                    placeholder="RCS Paris 123 456 789"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Adresse */}
                                    <div className="space-y-4">
                                        <h3 className="font-medium text-slate-700 flex items-center gap-2">
                                            <MapPin className="w-4 h-4" />
                                            Adresse
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="md:col-span-2 space-y-2">
                                                <Label htmlFor="valnexo_address">Adresse</Label>
                                                <Input
                                                    id="valnexo_address"
                                                    name="address"
                                                    value={valnexoSettings.address}
                                                    onChange={handleValnexoChange}
                                                    placeholder="123 Avenue de l'Énergie"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="valnexo_postal_code">Code postal</Label>
                                                <Input
                                                    id="valnexo_postal_code"
                                                    name="postal_code"
                                                    value={valnexoSettings.postal_code}
                                                    onChange={handleValnexoChange}
                                                    placeholder="75008"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="valnexo_city">Ville</Label>
                                                <Input
                                                    id="valnexo_city"
                                                    name="city"
                                                    value={valnexoSettings.city}
                                                    onChange={handleValnexoChange}
                                                    placeholder="Paris"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="valnexo_country">Pays</Label>
                                                <Input
                                                    id="valnexo_country"
                                                    name="country"
                                                    value={valnexoSettings.country}
                                                    onChange={handleValnexoChange}
                                                    placeholder="France"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Contact */}
                                    <div className="space-y-4">
                                        <h3 className="font-medium text-slate-700 flex items-center gap-2">
                                            <Mail className="w-4 h-4" />
                                            Contact
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="valnexo_phone">Téléphone</Label>
                                                <Input
                                                    id="valnexo_phone"
                                                    name="phone"
                                                    value={valnexoSettings.phone}
                                                    onChange={handleValnexoChange}
                                                    placeholder="+33 1 23 45 67 89"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="valnexo_email">Email</Label>
                                                <Input
                                                    id="valnexo_email"
                                                    name="email"
                                                    type="email"
                                                    value={valnexoSettings.email}
                                                    onChange={handleValnexoChange}
                                                    placeholder="contact@valnexo.com"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="valnexo_website">Site web</Label>
                                                <Input
                                                    id="valnexo_website"
                                                    name="website"
                                                    value={valnexoSettings.website}
                                                    onChange={handleValnexoChange}
                                                    placeholder="www.valnexo.com"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Coordonnées bancaires */}
                                    <div className="space-y-4">
                                        <h3 className="font-medium text-slate-700 flex items-center gap-2">
                                            <Banknote className="w-4 h-4" />
                                            Coordonnées bancaires (pour les factures)
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="valnexo_iban">IBAN</Label>
                                                <Input
                                                    id="valnexo_iban"
                                                    name="iban"
                                                    value={valnexoSettings.iban}
                                                    onChange={handleValnexoChange}
                                                    placeholder="FR76 1234 5678 9012 3456 7890 123"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="valnexo_bic">BIC</Label>
                                                <Input
                                                    id="valnexo_bic"
                                                    name="bic"
                                                    value={valnexoSettings.bic}
                                                    onChange={handleValnexoChange}
                                                    placeholder="BNPAFRPP"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="valnexo_bank_name">Nom de la banque</Label>
                                                <Input
                                                    id="valnexo_bank_name"
                                                    name="bank_name"
                                                    value={valnexoSettings.bank_name}
                                                    onChange={handleValnexoChange}
                                                    placeholder="BNP Paribas"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex justify-end pt-4 border-t">
                                        <Button onClick={saveValnexoSettings} className="bg-yellow-600 hover:bg-yellow-700" disabled={saving}>
                                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                                            Sauvegarder les paramètres Valnexo
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>
                    )}
                    
                    {/* AT Service Tab */}
                    {isAdminFull && (
                        <TabsContent value="at_service">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Building2 className="w-5 h-5 text-blue-600" />
                                        Paramètres AT Service
                                    </CardTitle>
                                    <CardDescription>
                                        Informations de la société AT Service utilisées sur les factures
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <Label htmlFor="at_company_name">Raison sociale *</Label>
                                            <Input id="at_company_name" name="company_name" value={atServiceSettings.company_name} onChange={handleAtServiceChange} />
                                        </div>
                                        <div>
                                            <Label htmlFor="at_siret">SIRET</Label>
                                            <Input id="at_siret" name="siret" value={atServiceSettings.siret} onChange={handleAtServiceChange} />
                                        </div>
                                        <div>
                                            <Label htmlFor="at_siren">SIREN</Label>
                                            <Input id="at_siren" name="siren" value={atServiceSettings.siren} onChange={handleAtServiceChange} />
                                        </div>
                                        <div>
                                            <Label htmlFor="at_tva">N° TVA Intracommunautaire</Label>
                                            <Input id="at_tva" name="tva_number" value={atServiceSettings.tva_number} onChange={handleAtServiceChange} />
                                        </div>
                                        <div>
                                            <Label htmlFor="at_capital">Capital social</Label>
                                            <Input id="at_capital" name="capital" value={atServiceSettings.capital} onChange={handleAtServiceChange} />
                                        </div>
                                        <div>
                                            <Label htmlFor="at_rcs">RCS</Label>
                                            <Input id="at_rcs" name="rcs" value={atServiceSettings.rcs} onChange={handleAtServiceChange} />
                                        </div>
                                    </div>
                                    
                                    <div className="border-t pt-4">
                                        <h4 className="font-medium mb-3">Adresse</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="md:col-span-2">
                                                <Label htmlFor="at_address">Adresse</Label>
                                                <Input id="at_address" name="address" value={atServiceSettings.address} onChange={handleAtServiceChange} />
                                            </div>
                                            <div>
                                                <Label htmlFor="at_postal_code">Code postal</Label>
                                                <Input id="at_postal_code" name="postal_code" value={atServiceSettings.postal_code} onChange={handleAtServiceChange} />
                                            </div>
                                            <div>
                                                <Label htmlFor="at_city">Ville</Label>
                                                <Input id="at_city" name="city" value={atServiceSettings.city} onChange={handleAtServiceChange} />
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="border-t pt-4">
                                        <h4 className="font-medium mb-3">Contact</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <Label htmlFor="at_phone">Téléphone</Label>
                                                <Input id="at_phone" name="phone" value={atServiceSettings.phone} onChange={handleAtServiceChange} />
                                            </div>
                                            <div>
                                                <Label htmlFor="at_email">Email</Label>
                                                <Input id="at_email" name="email" type="email" value={atServiceSettings.email} onChange={handleAtServiceChange} />
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="border-t pt-4">
                                        <h4 className="font-medium mb-3">Coordonnées bancaires</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <Label htmlFor="at_iban">IBAN</Label>
                                                <Input id="at_iban" name="iban" value={atServiceSettings.iban} onChange={handleAtServiceChange} />
                                            </div>
                                            <div>
                                                <Label htmlFor="at_bic">BIC</Label>
                                                <Input id="at_bic" name="bic" value={atServiceSettings.bic} onChange={handleAtServiceChange} />
                                            </div>
                                            <div>
                                                <Label htmlFor="at_bank_name">Nom de la banque</Label>
                                                <Input id="at_bank_name" name="bank_name" value={atServiceSettings.bank_name} onChange={handleAtServiceChange} />
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="border-t pt-4">
                                        <h4 className="font-medium mb-3 flex items-center gap-2">
                                            <span>Seuil de partage AT Service</span>
                                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                                                Courtage ≥ 01/07/2026
                                            </span>
                                        </h4>
                                        <p className="text-sm text-slate-600 mb-3">
                                            % maximum de la commission totale HT qu'AT Service accepte de partager
                                            en commissions VP + Apporteur. Au-delà de ce seuil, l'excédent est
                                            <strong> à la charge exclusive de Valnexo</strong>. Ne concerne que le
                                            module Courtage signé à partir du 01/07/2026 (split 65 / 35).
                                            Travaux d’optimisation reste 50 / 50 sans plafond.
                                        </p>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <Label htmlFor="at_max_share">Seuil de participation (%)</Label>
                                                <div className="relative">
                                                    <Input
                                                        id="at_max_share"
                                                        name="at_service_max_share_percent"
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        step="0.5"
                                                        value={atServiceSettings.at_service_max_share_percent ?? 30}
                                                        onChange={handleAtServiceChange}
                                                        className="pr-8"
                                                        data-testid="at-service-max-share-input"
                                                    />
                                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500 pointer-events-none">%</span>
                                                </div>
                                                <p className="text-xs text-slate-500 mt-1">Défaut : 30 %</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex justify-end pt-4">
                                        <Button onClick={saveAtServiceSettings} className="bg-blue-600 hover:bg-blue-700" disabled={saving}>
                                            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                                            Sauvegarder
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>
                    )}

                    {/* Payeurs Tab */}
                    {isAdminFull && (
                        <TabsContent value="payers">
                            <Card>
                                <CardHeader>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <CardTitle className="flex items-center gap-2">
                                                <Wallet className="w-5 h-5 text-purple-600" />
                                                Payeurs personnalisés
                                            </CardTitle>
                                            <CardDescription>
                                                Gérez les payeurs pour les factures (en plus de Valnexo et AT Service)
                                            </CardDescription>
                                        </div>
                                        <Button onClick={() => { setEditingPayer(null); setShowPayerDialog(true); }} className="bg-purple-600 hover:bg-purple-700">
                                            <Plus className="w-4 h-4 mr-2" />
                                            Ajouter un payeur
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    {payers.length === 0 ? (
                                        <div className="text-center py-8 text-slate-500">
                                            <Wallet className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                                            <p>Aucun payeur personnalisé</p>
                                            <p className="text-sm">Valnexo et AT Service sont disponibles par défaut</p>
                                        </div>
                                    ) : (
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Nom</TableHead>
                                                    <TableHead>Raison sociale</TableHead>
                                                    <TableHead>SIRET</TableHead>
                                                    <TableHead>Ville</TableHead>
                                                    <TableHead className="text-right">Actions</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {payers.map((payer) => (
                                                    <TableRow key={payer.id}>
                                                        <TableCell className="font-medium">{payer.name}</TableCell>
                                                        <TableCell>{payer.company_name}</TableCell>
                                                        <TableCell>{payer.siret || '-'}</TableCell>
                                                        <TableCell>{payer.city || '-'}</TableCell>
                                                        <TableCell className="text-right">
                                                            <Button size="sm" variant="ghost" onClick={() => handleEditPayer(payer)}>
                                                                <Pencil className="w-4 h-4" />
                                                            </Button>
                                                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeletePayer(payer.id)}>
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>
                    )}

                    {/* Users Tab */}
                    {canManageUsers && (
                        <TabsContent value="users">
                            {/* Panneau Autorisations Projets (Hub) — visible pour les admins */}
                            {(user?.role === 'admin_full' || user?.is_admin === true) && (
                                <div className="mb-4">
                                    <HubOverrideRequestsPanel />
                                </div>
                            )}
                            <Card>
                                <CardHeader>
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                        <div>
                                            <CardTitle className="flex items-center gap-2">
                                                <Shield className="w-5 h-5" />
                                                Gestion des accès
                                            </CardTitle>
                                            <CardDescription>Gérez les utilisateurs et leurs droits</CardDescription>
                                        </div>
                                        <div className="flex items-center gap-3 flex-wrap">
                                            <div className="flex items-center gap-2">
                                                <Switch checked={showInactive} onCheckedChange={setShowInactive} id="show-inactive" />
                                                <Label htmlFor="show-inactive" className="text-sm">Afficher inactifs</Label>
                                            </div>
                                            
                                            <Button className="bg-slate-900 hover:bg-slate-800" onClick={openCreateUserDialog}>
                                                <Plus className="w-4 h-4 mr-2" />
                                                Nouvel utilisateur
                                            </Button>
                                            
                                            <Dialog open={createUserDialog} onOpenChange={setCreateUserDialog}>
                                                <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden">
                                                    <DialogHeader>
                                                        <DialogTitle>Créer un utilisateur</DialogTitle>
                                                    </DialogHeader>
                                                    <div className="overflow-y-auto max-h-[calc(90vh-140px)] space-y-4 py-4 pr-2">
                                                        <div className="grid grid-cols-2 gap-4">
                                                            <div className="space-y-2">
                                                                <Label>Prénom</Label>
                                                                <Input value={newUser.first_name} onChange={(e) => setNewUser(prev => ({ ...prev, first_name: e.target.value }))} />
                                                            </div>
                                                            <div className="space-y-2">
                                                                <Label>Nom</Label>
                                                                <Input value={newUser.last_name} onChange={(e) => setNewUser(prev => ({ ...prev, last_name: e.target.value }))} />
                                                            </div>
                                                        </div>
                                                        <div className="space-y-2">
                                                            <Label>Email</Label>
                                                            <Input type="email" value={newUser.email} onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))} />
                                                        </div>
                                                        {/* Mot de passe : requis pour tous les rôles (parrain reçoit un e-mail avec ses identifiants) */}
                                                        <div className="space-y-2">
                                                            <Label>Mot de passe *</Label>
                                                            <div className="relative">
                                                                <Input 
                                                                    type={showNewUserPassword ? "text" : "password"} 
                                                                    value={newUser.password} 
                                                                    onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                                                                    placeholder="Minimum 6 caractères"
                                                                />
                                                                <button 
                                                                    type="button" 
                                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" 
                                                                    onClick={() => setShowNewUserPassword(!showNewUserPassword)}
                                                                    tabIndex={-1}
                                                                >
                                                                    {showNewUserPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <div className="space-y-2">
                                                            <Label>Confirmer le mot de passe *</Label>
                                                            <div className="relative">
                                                                <Input 
                                                                    type={showNewUserPassword ? "text" : "password"} 
                                                                    value={newUser.confirm_password} 
                                                                    onChange={(e) => setNewUser(prev => ({ ...prev, confirm_password: e.target.value }))}
                                                                    placeholder="Retapez le mot de passe"
                                                                    className={newUser.confirm_password && newUser.password !== newUser.confirm_password ? "border-red-500" : ""}
                                                                />
                                                                {newUser.confirm_password && newUser.password !== newUser.confirm_password && (
                                                                    <p className="text-xs text-red-500 mt-1">Les mots de passe ne correspondent pas</p>
                                                                )}
                                                                {newUser.confirm_password && newUser.password === newUser.confirm_password && newUser.password.length >= 6 && (
                                                                    <p className="text-xs text-green-500 mt-1">✓ Mots de passe identiques</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="space-y-2">
                                                            <Label>Téléphone</Label>
                                                            <Input value={newUser.phone} onChange={(e) => setNewUser(prev => ({ ...prev, phone: e.target.value }))} />
                                                        </div>
                                                        <div className="space-y-2">
                                                            <Label>Rôle</Label>
                                                            <Select value={newUser.role} onValueChange={(v) => setNewUser(prev => ({ ...prev, role: v, parent_apporteur_id: '' }))}>
                                                                <SelectTrigger><SelectValue /></SelectTrigger>
                                                                <SelectContent>
                                                                    {isVP ? (
                                                                        // VP : apporteur et parrain (module Parrainage désormais activé par défaut pour tous les VP)
                                                                        <>
                                                                            <SelectItem value="apporteur">Apporteur</SelectItem>
                                                                            <SelectItem value="parrain">Parrain</SelectItem>
                                                                        </>
                                                                    ) : (
                                                                        // Admin et Full Admin peuvent créer tous les rôles
                                                                        Object.entries(ROLE_NAMES).map(([key, label]) => (
                                                                            <SelectItem key={key} value={key}>{label}</SelectItem>
                                                                        ))
                                                                    )}
                                                                </SelectContent>
                                                            </Select>
                                                        </div>

                                                        {/* Champs spécifiques PARRAIN */}
                                                        {newUser.role === 'parrain' && (
                                                            <div className="space-y-3 rounded-lg border border-indigo-200 bg-indigo-50/30 p-3" data-testid="new-user-parrain-block">
                                                                <div className="text-xs font-semibold text-indigo-800">Paramètres Réseau Parrainage</div>
                                                                <div className="text-[11px] text-slate-600 bg-white/60 border border-indigo-100 rounded p-2">
                                                                    Le parrain recevra automatiquement un e-mail contenant son identifiant et le mot de passe que vous définirez ci-dessus.
                                                                </div>
                                                                {!isVP && (
                                                                    <div className="space-y-2">
                                                                        <Label className="text-xs">VP responsable *</Label>
                                                                        <Select
                                                                            value={newUser.vp_id || ''}
                                                                            onValueChange={(v) => { setNewUser(prev => ({ ...prev, vp_id: v, parrain_parent_id: '' })); loadExistingParrainsForVp(v); }}
                                                                        >
                                                                            <SelectTrigger data-testid="new-user-parrain-vp"><SelectValue placeholder="Choisir un VP (Réseau Parrainage activé)" /></SelectTrigger>
                                                                            <SelectContent>
                                                                                {parrainageVps.length === 0 && <div className="p-2 text-xs text-slate-500">Aucun VP avec le module Parrainage activé. Activez-le depuis la fiche VP.</div>}
                                                                                {parrainageVps.map(vp => (
                                                                                    <SelectItem key={vp.id} value={vp.id}>{vp.first_name} {vp.last_name}</SelectItem>
                                                                                ))}
                                                                            </SelectContent>
                                                                        </Select>
                                                                    </div>
                                                                )}
                                                                <div className="space-y-2">
                                                                    <Label className="text-xs">Niveau *</Label>
                                                                    <Select
                                                                        value={String(newUser.parrain_level)}
                                                                        onValueChange={(v) => setNewUser(prev => ({ ...prev, parrain_level: parseInt(v, 10), parrain_parent_id: '' }))}
                                                                    >
                                                                        <SelectTrigger data-testid="new-user-parrain-level"><SelectValue /></SelectTrigger>
                                                                        <SelectContent>
                                                                            <SelectItem value="1">Niveau 1 (racine)</SelectItem>
                                                                            <SelectItem value="2">Niveau 2</SelectItem>
                                                                            <SelectItem value="3">Niveau 3</SelectItem>
                                                                        </SelectContent>
                                                                    </Select>
                                                                </div>
                                                                {newUser.parrain_level === 1 ? (
                                                                    <div className="space-y-2">
                                                                        <Label className="text-xs">Nom de la branche *</Label>
                                                                        <Input value={newUser.parrain_branch}
                                                                            onChange={e => setNewUser(prev => ({ ...prev, parrain_branch: e.target.value }))}
                                                                            placeholder="ex : Réseau Sud"
                                                                            data-testid="new-user-parrain-branch" />
                                                                    </div>
                                                                ) : (
                                                                    <div className="space-y-2">
                                                                        <Label className="text-xs">Rattaché à (Niveau {newUser.parrain_level - 1}) *</Label>
                                                                        <Select
                                                                            value={newUser.parrain_parent_id || ''}
                                                                            onValueChange={(v) => setNewUser(prev => ({ ...prev, parrain_parent_id: v }))}
                                                                        >
                                                                            <SelectTrigger data-testid="new-user-parrain-parent"><SelectValue placeholder="Choisir le parrain de rattachement" /></SelectTrigger>
                                                                            <SelectContent>
                                                                                {existingParrains
                                                                                    .filter(p => p.parrain_level === (newUser.parrain_level - 1) && p.is_active)
                                                                                    .map(p => (
                                                                                        <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name} — {p.parrain_branch}</SelectItem>
                                                                                    ))}
                                                                                {existingParrains.filter(p => p.parrain_level === (newUser.parrain_level - 1) && p.is_active).length === 0 && (
                                                                                    <div className="p-2 text-xs text-slate-500">Aucun parrain N{newUser.parrain_level - 1} disponible {isVP ? "" : "sur ce VP"}.</div>
                                                                                )}
                                                                            </SelectContent>
                                                                        </Select>
                                                                    </div>
                                                                )}
                                                                {(isAdminFull || isAdmin) ? (
                                                                    <>
                                                                        <div className="grid grid-cols-2 gap-3">
                                                                            <div>
                                                                                <Label className="text-xs">% Courtage (optionnel)</Label>
                                                                                <Input type="number" step="0.1" min="14" max="100"
                                                                                    disabled={parseInt(newUser.parrain_level || 1, 10) !== 1}
                                                                                    value={newUser.commission_percent_ep_override}
                                                                                    onChange={e => setNewUser(prev => ({ ...prev, commission_percent_ep_override: e.target.value }))}
                                                                                    placeholder={parseInt(newUser.parrain_level || 1, 10) === 1 ? '≥ 14 (vide = taux global)' : 'N1 uniquement'}
                                                                                    data-testid="new-user-parrain-ep-override" />
                                                                            </div>
                                                                            <div>
                                                                                <Label className="text-xs">% Travaux d’optimisation (optionnel)</Label>
                                                                                <Input type="number" step="0.1" min="8" max="100"
                                                                                    disabled={parseInt(newUser.parrain_level || 1, 10) !== 1}
                                                                                    value={newUser.commission_percent_ch_override}
                                                                                    onChange={e => setNewUser(prev => ({ ...prev, commission_percent_ch_override: e.target.value }))}
                                                                                    placeholder={parseInt(newUser.parrain_level || 1, 10) === 1 ? '≥ 8 (vide = taux global)' : 'N1 uniquement'}
                                                                                    data-testid="new-user-parrain-ch-override" />
                                                                            </div>
                                                                        </div>
                                                                        <p className="text-[11px] text-slate-500">
                                                                            {parseInt(newUser.parrain_level || 1, 10) === 1
                                                                                ? "Override N1 = budget TOTAL sur toute la branche (le restant après distribution aux niveaux en dessous revient au N1). Doit être ≥ 14% Courtage et ≥ 8% Travaux d’optimisation."
                                                                                : "Un N2/N3 utilise toujours les taux globaux — pas de personnalisation possible."}
                                                                        </p>
                                                                    </>
                                                                ) : (
                                                                    <p className="text-[11px] text-slate-600 bg-indigo-50/60 border border-indigo-100 rounded p-2 leading-relaxed" data-testid="new-user-parrain-override-vp-notice">
                                                                        Ce parrain sera créé aux <b>taux globaux du réseau</b>. Si vous souhaitez lui donner plus, demandez à un administrateur.
                                                                    </p>
                                                                )}
                                                            </div>
                                                        )}
                                                        {newUser.role === 'apporteur' && !isVP && vps.length > 0 && (
                                                            <div className="space-y-2">
                                                                <Label>VP responsable</Label>
                                                                <Select value={newUser.vp_id || "none"} onValueChange={handleVpChange}>
                                                                    <SelectTrigger><SelectValue placeholder="Sélectionnez un VP" /></SelectTrigger>
                                                                    <SelectContent>
                                                                        <SelectItem value="none">Aucun (Apporteur libre)</SelectItem>
                                                                        {vps.map((vp) => (
                                                                            <SelectItem key={vp.id} value={vp.id}>{vp.first_name} {vp.last_name}</SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                                {(!newUser.vp_id || newUser.vp_id === 'none') && (
                                                                    <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                                                                        ⚠️ <strong>Apporteur libre</strong> : Travaille directement pour Valnexo/AT Service. 
                                                                        Sa commission est prélevée sur le total, le reste est partagé 50/50.
                                                                    </p>
                                                                )}
                                                            </div>
                                                        )}
                                                        
                                                        {/* VP créant un apporteur : message informatif */}
                                                        {newUser.role === 'apporteur' && isVP && (
                                                            <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                                                                <p className="text-xs text-blue-700">
                                                                    Cet apporteur sera automatiquement rattaché à votre compte VP.
                                                                </p>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Apporteur parent (optionnel) - pour créer une hiérarchie */}
                                                        {newUser.role === 'apporteur' && newUser.vp_id && availableParentApporteurs.length > 0 && (
                                                            <div className="space-y-2">
                                                                <Label className="flex items-center gap-2">
                                                                    <Users className="w-4 h-4 text-purple-500" />
                                                                    Apporteur parent (optionnel)
                                                                </Label>
                                                                <Select 
                                                                    value={newUser.parent_apporteur_id || "none"} 
                                                                    onValueChange={(v) => setNewUser(prev => ({ ...prev, parent_apporteur_id: v === "none" ? "" : v }))}
                                                                >
                                                                    <SelectTrigger><SelectValue placeholder="Aucun (directement sous le VP)" /></SelectTrigger>
                                                                    <SelectContent>
                                                                        <SelectItem value="none">Aucun (directement sous le VP)</SelectItem>
                                                                        {availableParentApporteurs.map((ap) => (
                                                                            <SelectItem key={ap.id} value={ap.id}>
                                                                                {ap.first_name} {ap.last_name} (N{ap.hierarchy_level || 1})
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                                <p className="text-xs text-slate-500">
                                                                    Sa commission sera déduite de celle de l'apporteur parent
                                                                </p>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Option Payé direct pour apporteur */}
                                                        {newUser.role === 'apporteur' && (
                                                            <div className="space-y-2 border-t pt-4 mt-4">
                                                                <Label className="flex items-center gap-2">
                                                                    <CreditCard className="w-4 h-4 text-green-600" />
                                                                    Payé direct (optionnel)
                                                                </Label>
                                                                <Select 
                                                                    value={newUser.direct_payer || "none"} 
                                                                    onValueChange={(v) => setNewUser(prev => ({ ...prev, direct_payer: v === "none" ? null : v }))}
                                                                >
                                                                    <SelectTrigger>
                                                                        <SelectValue placeholder="Payé par son VP (par défaut)" />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        <SelectItem value="none">
                                                                            <span className="text-slate-500">Payé par son VP (par défaut)</span>
                                                                        </SelectItem>
                                                                        {availablePayers.map((payer) => (
                                                                            <SelectItem key={payer.id} value={payer.id}>
                                                                                {payer.name}
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                                <p className="text-xs text-slate-500">
                                                                    Si cet apporteur a des sous-apporteurs, il sera payé directement par l'entité sélectionnée au lieu de son VP
                                                                </p>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Barème de commission obligatoire pour le VP uniquement */}
                                                        {newUser.role === 'vp' && (
                                                            <div className="border-t pt-4 mt-4">
                                                                <h4 className="font-medium mb-3 flex items-center gap-2">
                                                                    <Banknote className="w-4 h-4 text-amber-500" />
                                                                    Barèmes de commission obligatoires
                                                                </h4>
                                                                <p className="text-xs text-slate-500 mb-3">
                                                                    Pourcentage de la commission totale du dossier (0-100%)
                                                                </p>
                                                                <div className="grid grid-cols-2 gap-4">
                                                                    {/* Courtage */}
                                                                    <div className="p-3 border rounded-lg bg-amber-50">
                                                                        <Label className="flex items-center gap-2 text-amber-700 mb-2">
                                                                            <Zap className="w-4 h-4" />
                                                                            Courtage
                                                                        </Label>
                                                                        <div className="flex items-center gap-2">
                                                                            <Input 
                                                                                type="number"
                                                                                step="1"
                                                                                min="0"
                                                                                max="100"
                                                                                placeholder={newUser.role === 'vp' ? '30' : '20'}
                                                                                value={newUser.commission_rates?.easy_power?.value || ''}
                                                                                onChange={(e) => setNewUser(prev => ({
                                                                                    ...prev,
                                                                                    commission_rates: {
                                                                                        ...prev.commission_rates,
                                                                                        easy_power: { ...prev.commission_rates.easy_power, value: e.target.value }
                                                                                    }
                                                                                }))}
                                                                                className="flex-1"
                                                                            />
                                                                            <span className="text-sm font-medium text-amber-600">%</span>
                                                                        </div>
                                                                        <p className="text-xs text-amber-600 mt-1">
                                                                            {newUser.role === 'vp' ? '% de la com totale' : '% de la com apporteur'}
                                                                        </p>
                                                                    </div>
                                                                    
                                                                    {/* Travaux d’optimisation */}
                                                                    <div className="p-3 border rounded-lg bg-blue-50">
                                                                        <Label className="flex items-center gap-2 text-blue-700 mb-2">
                                                                            <HardHat className="w-4 h-4" />
                                                                            Travaux d’optimisation
                                                                        </Label>
                                                                        <div className="flex items-center gap-2">
                                                                            <Input 
                                                                                type="number"
                                                                                step="1"
                                                                                min="0"
                                                                                max="100"
                                                                                placeholder={newUser.role === 'vp' ? '30' : '25'}
                                                                                value={newUser.commission_rates?.chantier?.value || ''}
                                                                                onChange={(e) => setNewUser(prev => ({
                                                                                    ...prev,
                                                                                    commission_rates: {
                                                                                        ...prev.commission_rates,
                                                                                        chantier: { ...prev.commission_rates.chantier, value: e.target.value }
                                                                                    }
                                                                                }))}
                                                                                className="flex-1"
                                                                            />
                                                                            <span className="text-sm font-medium text-blue-600">%</span>
                                                                        </div>
                                                                        <p className="text-xs text-blue-600 mt-1">
                                                                            {newUser.role === 'vp' ? '% de la com totale' : '% de la com apporteur'}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Apporteur : commission gérée sur la page Équipe */}
                                                        {newUser.role === 'apporteur' && (
                                                            <div className="border-t pt-4 mt-4">
                                                                <div className="p-3 rounded-lg bg-blue-50 border border-blue-100" data-testid="new-user-apporteur-commission-info">
                                                                    <h4 className="font-medium mb-1 flex items-center gap-2 text-blue-800 text-sm">
                                                                        <Banknote className="w-4 h-4" />
                                                                        Commission de l'apporteur
                                                                    </h4>
                                                                    <p className="text-xs text-blue-600">
                                                                        Le pourcentage de cet apporteur se règle dans son équipe
                                                                        (page <span className="font-medium">Équipe → « Modifier commission »</span>),
                                                                        pour Courtage et Travaux d’optimisation. C'est l'unique source utilisée dans les calculs.
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <DialogFooter className="border-t pt-4 bg-white">
                                                        <Button variant="outline" onClick={() => setCreateUserDialog(false)}>Annuler</Button>
                                                        <Button onClick={handleCreateUser} disabled={saving}>
                                                            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                                                            Créer
                                                        </Button>
                                                    </DialogFooter>
                                                </DialogContent>
                                            </Dialog>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <Table containerClassName="max-h-[70vh] rounded-md border border-slate-100" className="min-w-[1100px]" data-testid="users-table-scroll">
                                            <TableHeader className="sticky top-0 z-10 bg-white shadow-sm">
                                                <TableRow>
                                                    <TableHead>Utilisateur</TableHead>
                                                    <TableHead>Email</TableHead>
                                                    <TableHead>Rôle</TableHead>
                                                    <TableHead>Hiérarchie</TableHead>
                                                    <TableHead>Barèmes</TableHead>
                                                    <TableHead>Statut</TableHead>
                                                    <TableHead className="text-right">Actions</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {allUsers.map((u) => (
                                                    <TableRow key={u.id} className={u.is_active === false ? 'opacity-60' : ''}>
                                                        <TableCell>
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                                                                    <span className="text-sm font-medium">{u.first_name?.[0]}{u.last_name?.[0]}</span>
                                                                </div>
                                                                <div>
                                                                    <p className="font-medium">{u.first_name} {u.last_name}</p>
                                                                    {u.phone && <p className="text-xs text-slate-500">{u.phone}</p>}
                                                                </div>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>{u.email}</TableCell>
                                                        <TableCell>
                                                            {(user?.role === 'admin_full' || user?.is_admin === true) && u.id !== user?.id ? (
                                                                <Select value={u.role} onValueChange={(v) => handleRoleChange(u.id, v)}>
                                                                    <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                                                                    <SelectContent>
                                                                        {Object.entries(ROLE_NAMES).map(([key, label]) => (
                                                                            <SelectItem key={key} value={key}>{label}</SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                            ) : (
                                                                <Badge className={ROLE_COLORS[u.role]}>{ROLE_NAMES[u.role]}</Badge>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {u.role === 'apporteur' ? (
                                                                <div className="text-xs space-y-0.5">
                                                                    {u.vp_name && (
                                                                        <div className="flex items-center gap-1 text-purple-600">
                                                                            <span>VP: {u.vp_name}</span>
                                                                        </div>
                                                                    )}
                                                                    {u.parent_apporteur_name && (
                                                                        <div className="flex items-center gap-1 text-blue-600">
                                                                            <span>Parent: {u.parent_apporteur_name}</span>
                                                                        </div>
                                                                    )}
                                                                    <Badge variant="outline" className="text-xs">
                                                                        N{u.hierarchy_level || 1}
                                                                    </Badge>
                                                                </div>
                                                            ) : u.role === 'vp' ? (
                                                                <Badge className="bg-purple-100 text-purple-700">Chef d'équipe</Badge>
                                                            ) : (
                                                                <span className="text-xs text-slate-400">N/A</span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {u.role === 'vp' ? (
                                                                <div className="flex items-center gap-2">
                                                                    <div className="text-xs space-y-0.5">
                                                                        {u.commission_rates?.easy_power?.value !== undefined && (
                                                                            <div className="flex items-center gap-1">
                                                                                <Zap className="w-3 h-3 text-amber-500" />
                                                                                <span>Courtage : {u.commission_rates.easy_power.value}%</span>
                                                                            </div>
                                                                        )}
                                                                        {u.commission_rates?.chantier?.value !== undefined && (
                                                                            <div className="flex items-center gap-1">
                                                                                <HardHat className="w-3 h-3 text-blue-500" />
                                                                                <span>Travaux : {u.commission_rates.chantier.value}%</span>
                                                                            </div>
                                                                        )}
                                                                        {!u.commission_rates?.easy_power?.value && !u.commission_rates?.chantier?.value && (
                                                                            <span className="text-slate-400">Non défini</span>
                                                                        )}
                                                                    </div>
                                                                    {(user?.role === 'admin_full' || user?.is_admin === true) && (
                                                                        <Button 
                                                                            variant="ghost" 
                                                                            size="sm" 
                                                                            onClick={() => openEditCommissionDialog(u)}
                                                                            className="h-7 w-7 p-0"
                                                                            data-testid={`edit-commission-${u.id}`}
                                                                        >
                                                                            <Banknote className="w-4 h-4 text-amber-600" />
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            ) : u.role === 'apporteur' ? (
                                                                <span className="text-xs text-blue-600" data-testid={`apporteur-team-commission-${u.id}`}>
                                                                    Géré par l'équipe
                                                                </span>
                                                            ) : (
                                                                <span className="text-xs text-slate-400">N/A</span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {u.is_active === false ? (
                                                                <Badge variant="destructive" className="flex items-center gap-1 w-fit">
                                                                    <Snowflake className="w-3 h-3" />
                                                                    Gelé
                                                                </Badge>
                                                            ) : (
                                                                <Badge className="bg-green-100 text-green-700">Actif</Badge>
                                                            )}
                                                            {u.role === 'apporteur' && u.training_access_level && u.training_access_level !== 'full' && (
                                                                <Badge
                                                                    className={
                                                                        "flex items-center gap-1 w-fit mt-1 " +
                                                                        (u.training_access_level === 'disabled'
                                                                            ? "bg-red-100 text-red-700"
                                                                            : "bg-amber-100 text-amber-700")
                                                                    }
                                                                    data-testid={`training-badge-${u.id}`}
                                                                >
                                                                    <GraduationCap className="w-3 h-3" />
                                                                    {u.training_access_level === 'disabled' ? 'Désactivé (0 module)' : 'Formation incomplète'}
                                                                    {u.training_total_modules != null && ` ${u.training_completed_modules}/${u.training_total_modules}`}
                                                                </Badge>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            {u.id !== user?.id && (user?.role === 'admin_full' || user?.is_admin === true) && (
                                                                <div className="flex items-center justify-end gap-2">
                                                                    {/* Bouton Modifier */}
                                                                    <Button 
                                                                        variant="outline" 
                                                                        size="sm" 
                                                                        onClick={() => setEditUserDialog({ open: true, user: u })}
                                                                        className="text-slate-600"
                                                                        data-testid={`edit-user-${u.id}`}
                                                                    >
                                                                        <Pencil className="w-4 h-4 mr-1" />
                                                                        Modifier
                                                                    </Button>
                                                                    
                                                                    {/* Bouton Geler/Dégeler */}
                                                                    {u.is_active === false ? (
                                                                        <Button variant="outline" size="sm" onClick={() => handleUnfreezeUser(u.id)} className="text-green-600">
                                                                            Dégeler
                                                                        </Button>
                                                                    ) : (
                                                                        <Button variant="outline" size="sm" onClick={() => { setFreezeDialog({ open: true, user: u }); setFreezeData({ reason: '', until: '', isPermanent: true }); }} className="text-blue-600">
                                                                            <Snowflake className="w-4 h-4 mr-1" />
                                                                            Geler
                                                                        </Button>
                                                                    )}
                                                                    {/* Bouton Réactiver Académie (apporteur désactivé d'office 0 module) */}
                                                                    {u.role === 'apporteur' && u.training_access_level === 'disabled' && (
                                                                        <Button variant="outline" size="sm" onClick={() => handleGrantAcademyAccess(u.id)} className="text-amber-600 hover:bg-amber-50" data-testid={`grant-academy-${u.id}`}>
                                                                            <GraduationCap className="w-4 h-4 mr-1" />
                                                                            Réactiver Académie
                                                                        </Button>
                                                                    )}
                                                                    {/* Toggle rôle Auditeur (Admin Full uniquement) */}
                                                                    {(user?.role === 'admin_full' || user?.is_admin === true) && (
                                                                        <Button
                                                                            variant="outline"
                                                                            size="sm"
                                                                            onClick={() => handleToggleAuditor(u)}
                                                                            className={u.is_auditor ? "text-teal-700 bg-teal-50 hover:bg-teal-100 border-teal-300" : "text-slate-600 hover:bg-teal-50"}
                                                                            data-testid={`toggle-auditor-${u.id}`}
                                                                            title={u.is_auditor ? "Retirer le rôle Auditeur" : "Attribuer le rôle Auditeur"}
                                                                        >
                                                                            <ShieldCheck className="w-4 h-4 mr-1" />
                                                                            {u.is_auditor ? "Auditeur ✓" : "Auditeur"}
                                                                        </Button>
                                                                    )}
                                                                    {/* Accès Hub Projets (Admin Full uniquement) */}
                                                                    {(user?.role === 'admin_full' || user?.is_admin === true) && (
                                                                        <HubAccessButton targetUser={u} onChanged={loadUsers} />
                                                                    )}
                                                                    {/* Bouton Supprimer définitivement */}
                                                                    <Button variant="outline" size="sm" onClick={() => { setDeleteDialog({ open: true, user: u }); setDeleteConfirm(''); }} className="text-red-600 hover:bg-red-50" data-testid={`delete-user-${u.id}`}>
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </Button>
                                                                </div>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                </CardContent>
                            </Card>

                            <Dialog open={freezeDialog.open} onOpenChange={(open) => setFreezeDialog({ open, user: freezeDialog.user })}>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle>Geler le compte</DialogTitle>
                                        <DialogDescription>
                                            {freezeDialog.user?.first_name} {freezeDialog.user?.last_name} ne pourra plus se connecter
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-4 py-4">
                                        <div className="space-y-2">
                                            <Label>Raison (optionnel)</Label>
                                            <Textarea value={freezeData.reason} onChange={(e) => setFreezeData(prev => ({ ...prev, reason: e.target.value }))} placeholder="Ex: Non-respect des conditions..." />
                                        </div>
                                        <div className="space-y-3">
                                            <Label>Durée du gel</Label>
                                            <div className="flex gap-2">
                                                <Button
                                                    type="button"
                                                    variant={freezeData.isPermanent ? "default" : "outline"}
                                                    size="sm"
                                                    onClick={() => setFreezeData(prev => ({ ...prev, isPermanent: true, until: '' }))}
                                                    className={freezeData.isPermanent ? "bg-red-600 hover:bg-red-700" : ""}
                                                >
                                                    Permanent
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant={!freezeData.isPermanent ? "default" : "outline"}
                                                    size="sm"
                                                    onClick={() => setFreezeData(prev => ({ ...prev, isPermanent: false }))}
                                                >
                                                    Temporaire
                                                </Button>
                                            </div>
                                            {!freezeData.isPermanent && (
                                                <div className="space-y-1">
                                                    <Label className="text-sm text-slate-600">Jusqu'au</Label>
                                                    <Input type="date" value={freezeData.until} onChange={(e) => setFreezeData(prev => ({ ...prev, until: e.target.value }))} min={new Date().toISOString().split('T')[0]} />
                                                </div>
                                            )}
                                            {freezeData.isPermanent && (
                                                <p className="text-xs text-red-500 font-medium">Le compte sera gelé indéfiniment jusqu'à dégel manuel.</p>
                                            )}
                                        </div>
                                    </div>
                                    <DialogFooter>
                                        <Button variant="outline" onClick={() => setFreezeDialog({ open: false, user: null })}>Annuler</Button>
                                        <Button variant="destructive" onClick={handleFreezeUser} disabled={saving || (!freezeData.isPermanent && !freezeData.until)}>
                                            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                                            Geler le compte
                                        </Button>
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>

                            {/* Dialog Suppression Définitive */}
                            <Dialog open={deleteDialog.open} onOpenChange={(open) => { setDeleteDialog({ open, user: deleteDialog.user }); if (!open) setDeleteConfirm(''); }}>
                                <DialogContent data-testid="delete-user-dialog">
                                    <DialogHeader>
                                        <DialogTitle className="text-red-600 flex items-center gap-2">
                                            <AlertTriangle className="w-5 h-5" />
                                            Supprimer définitivement
                                        </DialogTitle>
                                        <DialogDescription>
                                            Cette action est irréversible. Le compte de <strong>{deleteDialog.user?.first_name} {deleteDialog.user?.last_name}</strong> sera supprimé.
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-4 py-4">
                                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                                            <p className="font-medium mb-1">Ce qui sera conservé :</p>
                                            <ul className="list-disc ml-4 space-y-0.5">
                                                <li>Tous les dossiers et prospects</li>
                                                <li>Toutes les commissions calculées</li>
                                                <li data-testid="delete-user-history-notice">L’ancien rattachement est conservé dans l’historique.</li>
                                            </ul>
                                            <p className="mt-3" data-testid="delete-user-transfer-rule">Apporteur : dossiers transférés à son VP. VP : dossiers transférés à son VP superviseur. Sans destinataire valide, les dossiers deviennent libres. Les dossiers en corbeille y restent.</p>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Tapez l'email pour confirmer : <span className="font-mono text-red-600">{deleteDialog.user?.email}</span></Label>
                                            <Input 
                                                value={deleteConfirm} 
                                                onChange={(e) => setDeleteConfirm(e.target.value)} 
                                                placeholder={deleteDialog.user?.email}
                                                data-testid="delete-confirm-input"
                                            />
                                        </div>
                                    </div>
                                    <DialogFooter>
                                        <Button variant="outline" onClick={() => setDeleteDialog({ open: false, user: null })}>Annuler</Button>
                                        <Button 
                                            variant="destructive" 
                                            onClick={handleDeleteUser} 
                                            disabled={saving || deleteConfirm !== deleteDialog.user?.email}
                                            data-testid="confirm-delete-user-btn"
                                        >
                                            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                                            Supprimer définitivement
                                        </Button>
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>

                            {/* Edit Commission Rates Dialog */}
                            <Dialog open={editCommissionDialog.open} onOpenChange={(open) => setEditCommissionDialog({ open, user: editCommissionDialog.user })}>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle className="flex items-center gap-2">
                                            <Banknote className="w-5 h-5 text-amber-500" />
                                            Modifier les barèmes
                                        </DialogTitle>
                                        <DialogDescription>
                                            {editCommissionDialog.user?.first_name} {editCommissionDialog.user?.last_name} ({ROLE_NAMES[editCommissionDialog.user?.role]})
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-4 py-4">
                                        <p className="text-sm text-slate-600">
                                            Pourcentage de la commission totale du dossier (0-100%)
                                        </p>
                                        <div className="grid grid-cols-2 gap-4">
                                            {/* Courtage */}
                                            <div className="p-3 border rounded-lg bg-amber-50">
                                                <Label className="flex items-center gap-2 text-amber-700 mb-2">
                                                    <Zap className="w-4 h-4" />
                                                    Courtage
                                                </Label>
                                                <div className="flex items-center gap-2">
                                                    <Input 
                                                        type="number"
                                                        step="1"
                                                        min="0"
                                                        max="100"
                                                        value={editCommissionData.easy_power.value}
                                                        onChange={(e) => setEditCommissionData(prev => ({
                                                            ...prev,
                                                            easy_power: { value: e.target.value }
                                                        }))}
                                                        className="flex-1"
                                                        data-testid="edit-commission-ep"
                                                    />
                                                    <span className="text-sm font-medium text-amber-600">%</span>
                                                </div>
                                                <p className="text-xs text-amber-600 mt-1">% de la com totale</p>
                                            </div>
                                            
                                            {/* Travaux d’optimisation */}
                                            <div className="p-3 border rounded-lg bg-blue-50">
                                                <Label className="flex items-center gap-2 text-blue-700 mb-2">
                                                    <HardHat className="w-4 h-4" />
                                                    Travaux d’optimisation
                                                </Label>
                                                <div className="flex items-center gap-2">
                                                    <Input 
                                                        type="number"
                                                        step="1"
                                                        min="0"
                                                        max="100"
                                                        value={editCommissionData.chantier.value}
                                                        onChange={(e) => setEditCommissionData(prev => ({
                                                            ...prev,
                                                            chantier: { value: e.target.value }
                                                        }))}
                                                        className="flex-1"
                                                        data-testid="edit-commission-ch"
                                                    />
                                                    <span className="text-sm font-medium text-blue-600">%</span>
                                                </div>
                                                <p className="text-xs text-blue-600 mt-1">% de la com totale</p>
                                            </div>
                                        </div>
                                    </div>
                                    <DialogFooter>
                                        <Button variant="outline" onClick={() => setEditCommissionDialog({ open: false, user: null })}>Annuler</Button>
                                        <Button onClick={handleUpdateCommissionRates} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
                                            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                                            Enregistrer
                                        </Button>
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>
                        </TabsContent>
                    )}

                    {/* Teams Tab */}
                    {canManageTeams && (
                        <TabsContent value="teams">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                <Card className="lg:col-span-1">
                                    <CardHeader>
                                        <div className="flex items-center justify-between">
                                            <CardTitle className="text-lg">Mes équipes</CardTitle>
                                            <Dialog open={createTeamDialog} onOpenChange={setCreateTeamDialog}>
                                                <DialogTrigger asChild>
                                                    <Button size="sm" variant="outline"><Plus className="w-4 h-4" /></Button>
                                                </DialogTrigger>
                                                <DialogContent>
                                                    <DialogHeader>
                                                        <DialogTitle>Créer une équipe</DialogTitle>
                                                    </DialogHeader>
                                                    <div className="space-y-4 py-4">
                                                        <div className="space-y-2">
                                                            <Label>Nom de l'équipe</Label>
                                                            <Input value={newTeam.name} onChange={(e) => setNewTeam(prev => ({ ...prev, name: e.target.value }))} placeholder="Ex: Équipe Sud-Ouest" />
                                                        </div>
                                                        <div className="space-y-2">
                                                            <Label>Description</Label>
                                                            <Textarea value={newTeam.description} onChange={(e) => setNewTeam(prev => ({ ...prev, description: e.target.value }))} placeholder="Description..." />
                                                        </div>
                                                    </div>
                                                    <DialogFooter>
                                                        <Button variant="outline" onClick={() => setCreateTeamDialog(false)}>Annuler</Button>
                                                        <Button onClick={handleCreateTeam} disabled={saving || !newTeam.name}>
                                                            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                                                            Créer
                                                        </Button>
                                                    </DialogFooter>
                                                </DialogContent>
                                            </Dialog>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        {teams.length === 0 ? (
                                            <p className="text-sm text-slate-500 text-center py-8">Aucune équipe</p>
                                        ) : (
                                            <div className="space-y-2">
                                                {teams.map((team) => (
                                                    <button
                                                        key={team.id}
                                                        onClick={() => loadTeamDetails(team.id)}
                                                        className={`w-full text-left p-3 rounded-lg transition-colors ${
                                                            selectedTeam?.id === team.id ? 'bg-slate-900 text-white' : 'bg-slate-50 hover:bg-slate-100'
                                                        }`}
                                                    >
                                                        <p className="font-medium">{team.name}</p>
                                                        <p className={`text-xs ${selectedTeam?.id === team.id ? 'text-slate-300' : 'text-slate-500'}`}>
                                                            {team.members?.length || 0} membre(s)
                                                        </p>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>

                                <Card className="lg:col-span-2">
                                    <CardHeader>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <CardTitle>{selectedTeam ? selectedTeam.name : 'Sélectionnez une équipe'}</CardTitle>
                                                {selectedTeam?.description && <CardDescription>{selectedTeam.description}</CardDescription>}
                                            </div>
                                            {selectedTeam && (
                                                <div className="flex gap-2">
                                                    <Dialog open={addMemberDialog} onOpenChange={setAddMemberDialog}>
                                                        <DialogTrigger asChild>
                                                            <Button size="sm" variant="outline">
                                                                <UserPlus className="w-4 h-4 mr-1" />
                                                                Ajouter
                                                            </Button>
                                                        </DialogTrigger>
                                                        <DialogContent>
                                                            <DialogHeader>
                                                                <DialogTitle>Ajouter un membre</DialogTitle>
                                                            </DialogHeader>
                                                            <div className="space-y-4 py-4">
                                                                <div className="space-y-2">
                                                                    <Label>Membre</Label>
                                                                    <Select value={newMember.user_id} onValueChange={(v) => setNewMember(prev => ({ ...prev, user_id: v }))}>
                                                                        <SelectTrigger><SelectValue placeholder="Sélectionnez..." /></SelectTrigger>
                                                                        <SelectContent>
                                                                            {availableUsersForTeam.map((u) => (
                                                                                <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name} ({ROLE_NAMES[u.role]})</SelectItem>
                                                                            ))}
                                                                        </SelectContent>
                                                                    </Select>
                                                                </div>
                                                                <div className="space-y-2">
                                                                    <Label>Taux de commission (%)</Label>
                                                                    <Input type="number" step="0.5" value={newMember.commission_rate} onChange={(e) => setNewMember(prev => ({ ...prev, commission_rate: e.target.value }))} placeholder="Ex: 15" />
                                                                </div>
                                                            </div>
                                                            <DialogFooter>
                                                                <Button variant="outline" onClick={() => setAddMemberDialog(false)}>Annuler</Button>
                                                                <Button onClick={handleAddMember} disabled={saving || !newMember.user_id}>
                                                                    {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                                                                    Ajouter
                                                                </Button>
                                                            </DialogFooter>
                                                        </DialogContent>
                                                    </Dialog>
                                                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeleteTeam(selectedTeam.id)}>
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        {!selectedTeam ? (
                                            <div className="text-center py-12 text-slate-500">
                                                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                                <p>Sélectionnez une équipe</p>
                                            </div>
                                        ) : teamMembers.length === 0 ? (
                                            <div className="text-center py-12 text-slate-500">
                                                <p>Aucun membre</p>
                                            </div>
                                        ) : (
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead>Membre</TableHead>
                                                        <TableHead>Rôle</TableHead>
                                                        <TableHead>Commission Courtage</TableHead>
                                                        <TableHead>Commission Travaux</TableHead>
                                                        <TableHead className="text-right">Actions</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {teamMembers.map((member) => (
                                                        <TableRow 
                                                            key={member.id}
                                                            className="cursor-pointer hover:bg-slate-50 transition-colors"
                                                            onClick={() => handleOpenMemberDashboard(member.id)}
                                                        >
                                                            <TableCell>
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                                                                        <span className="text-xs font-medium">{member.first_name?.[0]}{member.last_name?.[0]}</span>
                                                                    </div>
                                                                    <div>
                                                                        <p className="font-medium text-sm flex items-center gap-2">
                                                                            {member.first_name} {member.last_name}
                                                                            <ChevronRight className="w-4 h-4 text-slate-400" />
                                                                        </p>
                                                                        <p className="text-xs text-slate-500">{member.email}</p>
                                                                    </div>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Badge className={ROLE_COLORS[member.role]}>{ROLE_NAMES[member.role]}</Badge>
                                                                {member.is_vp_payer && <Badge className="ml-1 bg-emerald-100 text-emerald-700">VP Payeur</Badge>}
                                                                {member.is_vp_supervisor && <Badge className="ml-1 bg-purple-100 text-purple-700">VP Sup.</Badge>}
                                                            </TableCell>
                                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                                <div className="flex items-center gap-1">
                                                                    <span className="text-sm font-medium">{member.team_commission_ep || 0}%</span>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                                <div className="flex items-center gap-1">
                                                                    <span className="text-sm font-medium">{member.team_commission_ch || 0}%</span>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                                                                <div className="flex items-center justify-end gap-2">
                                                                    <Button 
                                                                        variant="outline" 
                                                                        size="sm" 
                                                                        className="text-blue-600"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleOpenMemberDashboard(member.id);
                                                                        }}
                                                                    >
                                                                        <Target className="w-4 h-4 mr-1" />
                                                                        Pilotage
                                                                    </Button>
                                                                    {!member.is_vp_payer && !member.is_vp_supervisor && (
                                                                        <Button 
                                                                            variant="ghost" 
                                                                            size="sm" 
                                                                            className="text-red-600" 
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleRemoveMember(member.id);
                                                                            }}
                                                                        >
                                                                            <Trash2 className="w-4 h-4" />
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>
                            
                            {/* Modal de Pilotage Membre */}
                            <Dialog open={memberDashboardOpen} onOpenChange={setMemberDashboardOpen}>
                                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                                    {loadingMemberDashboard ? (
                                        <div className="flex items-center justify-center py-12">
                                            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                                            <span className="ml-2 text-slate-500">Chargement...</span>
                                        </div>
                                    ) : selectedMemberDashboard && (
                                        <>
                                            <DialogHeader>
                                                <DialogTitle className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                                        <User className="w-5 h-5 text-blue-600" />
                                                    </div>
                                                    <div>
                                                        <span className="text-xl">{selectedMemberDashboard.member.name}</span>
                                                        <Badge className={`ml-2 ${ROLE_COLORS[selectedMemberDashboard.member.role]}`}>
                                                            {ROLE_NAMES[selectedMemberDashboard.member.role]}
                                                        </Badge>
                                                    </div>
                                                </DialogTitle>
                                                <DialogDescription>
                                                    Tableau de bord de pilotage - {selectedMemberDashboard.member.email}
                                                </DialogDescription>
                                            </DialogHeader>
                                            
                                            {/* Stats Cards */}
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 my-4">
                                                <Card className="bg-blue-50 border-blue-200">
                                                    <CardContent className="p-4">
                                                        <div className="flex items-center gap-2 text-blue-600 mb-1">
                                                            <Folder className="w-4 h-4" />
                                                            <span className="text-xs font-medium">Total Dossiers</span>
                                                        </div>
                                                        <p className="text-2xl font-bold text-blue-700">{selectedMemberDashboard.stats.total_dossiers}</p>
                                                    </CardContent>
                                                </Card>
                                                
                                                <Card className="bg-amber-50 border-amber-200">
                                                    <CardContent className="p-4">
                                                        <div className="flex items-center gap-2 text-amber-600 mb-1">
                                                            <Clock className="w-4 h-4" />
                                                            <span className="text-xs font-medium">En cours</span>
                                                        </div>
                                                        <p className="text-2xl font-bold text-amber-700">{selectedMemberDashboard.stats.en_cours}</p>
                                                    </CardContent>
                                                </Card>
                                                
                                                <Card className="bg-green-50 border-green-200">
                                                    <CardContent className="p-4">
                                                        <div className="flex items-center gap-2 text-green-600 mb-1">
                                                            <Trophy className="w-4 h-4" />
                                                            <span className="text-xs font-medium">Gagnés</span>
                                                        </div>
                                                        <p className="text-2xl font-bold text-green-700">{selectedMemberDashboard.stats.gagnes}</p>
                                                    </CardContent>
                                                </Card>
                                                
                                                <Card className="bg-slate-50 border-slate-200">
                                                    <CardContent className="p-4">
                                                        <div className="flex items-center gap-2 text-slate-600 mb-1">
                                                            <TrendingUp className="w-4 h-4" />
                                                            <span className="text-xs font-medium">Taux conversion</span>
                                                        </div>
                                                        <p className="text-2xl font-bold text-slate-700">{selectedMemberDashboard.stats.taux_conversion}%</p>
                                                    </CardContent>
                                                </Card>
                                            </div>
                                            
                                            {/* Commissions Summary */}
                                            <div className="grid grid-cols-2 gap-4 mb-4">
                                                <Card className="border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50">
                                                    <CardContent className="p-4">
                                                        <div className="flex items-center gap-2 text-amber-700 mb-2">
                                                            <Euro className="w-5 h-5" />
                                                            <span className="font-medium">Commissions en cours</span>
                                                        </div>
                                                        <p className="text-2xl font-bold text-amber-800">
                                                            {formatCurrency(selectedMemberDashboard.stats.commission_en_cours_total)}
                                                        </p>
                                                        <div className="flex gap-4 mt-2 text-sm text-amber-600">
                                                            <span>Courtage : {formatCurrency(selectedMemberDashboard.stats.commission_en_cours_ep)}</span>
                                                            <span>Travaux : {formatCurrency(selectedMemberDashboard.stats.commission_en_cours_ch)}</span>
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                                
                                                <Card className="border-green-300 bg-gradient-to-br from-green-50 to-emerald-50">
                                                    <CardContent className="p-4">
                                                        <div className="flex items-center gap-2 text-green-700 mb-2">
                                                            <Banknote className="w-5 h-5" />
                                                            <span className="font-medium">Commissions gagnées</span>
                                                        </div>
                                                        <p className="text-2xl font-bold text-green-800">
                                                            {formatCurrency(selectedMemberDashboard.stats.commission_gagnee_total)}
                                                        </p>
                                                        <div className="flex gap-4 mt-2 text-sm text-green-600">
                                                            <span>Courtage : {formatCurrency(selectedMemberDashboard.stats.commission_gagnee_ep)}</span>
                                                            <span>Travaux : {formatCurrency(selectedMemberDashboard.stats.commission_gagnee_ch)}</span>
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            </div>
                                            
                                            {/* Dossiers Tabs */}
                                            <Tabs defaultValue="en_cours" className="w-full">
                                                <TabsList className="grid w-full grid-cols-3">
                                                    <TabsTrigger value="en_cours" className="flex items-center gap-2">
                                                        <Clock className="w-4 h-4" />
                                                        En cours ({selectedMemberDashboard.dossiers.en_cours.length})
                                                    </TabsTrigger>
                                                    <TabsTrigger value="gagnes" className="flex items-center gap-2">
                                                        <Trophy className="w-4 h-4" />
                                                        Gagnés ({selectedMemberDashboard.dossiers.gagnes.length})
                                                    </TabsTrigger>
                                                    <TabsTrigger value="perdus" className="flex items-center gap-2">
                                                        <XCircle className="w-4 h-4" />
                                                        Perdus ({selectedMemberDashboard.dossiers.perdus.length})
                                                    </TabsTrigger>
                                                </TabsList>
                                                
                                                {['en_cours', 'gagnes', 'perdus'].map((category) => (
                                                    <TabsContent key={category} value={category} className="mt-4">
                                                        {selectedMemberDashboard.dossiers[category].length === 0 ? (
                                                            <div className="text-center py-8 text-slate-500">
                                                                <Folder className="w-12 h-12 mx-auto mb-2 opacity-30" />
                                                                <p>Aucun dossier {category === 'en_cours' ? 'en cours' : category === 'gagnes' ? 'gagné' : 'perdu'}</p>
                                                            </div>
                                                        ) : (
                                                            <div className="space-y-2 max-h-60 overflow-y-auto">
                                                                {selectedMemberDashboard.dossiers[category].map((dossier) => (
                                                                    <div 
                                                                        key={dossier.id}
                                                                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                                                                        onClick={() => {
                                                                            navigate(`/prospects/${dossier.id}`);
                                                                            setMemberDashboardOpen(false);
                                                                        }}
                                                                    >
                                                                        <div className="flex-1">
                                                                            <p className="font-medium text-sm flex items-center gap-2">
                                                                                {dossier.name}
                                                                                {dossier.is_vp_direct && (
                                                                                    <Badge className="bg-purple-100 text-purple-700 text-xs">VP Direct</Badge>
                                                                                )}
                                                                            </p>
                                                                            <div className="flex items-center gap-2 mt-1">
                                                                                {dossier.ep_stage && (
                                                                                    <Badge className={`text-xs ${getStageBadgeColor(dossier.ep_stage)}`}>
                                                                                        Courtage : {getStageLabel(dossier.ep_stage)}
                                                                                    </Badge>
                                                                                )}
                                                                                {dossier.ch_stage && (
                                                                                    <Badge className={`text-xs ${getStageBadgeColor(dossier.ch_stage)}`}>
                                                                                        Travaux : {getStageLabel(dossier.ch_stage)}
                                                                                    </Badge>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                        <div className="text-right">
                                                                            <p className="font-medium text-sm text-slate-700">
                                                                                {formatCurrency((dossier.commission_ep || 0) + (dossier.commission_ch || 0))}
                                                                            </p>
                                                                            <ExternalLink className="w-4 h-4 text-slate-400 ml-auto" />
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </TabsContent>
                                                ))}
                                            </Tabs>
                                        </>
                                    )}
                                </DialogContent>
                            </Dialog>

                        </TabsContent>
                    )}

                    {/* Knowledge Base Tab - Admin Full Only */}
                    {isAdminFull && (
                        <TabsContent value="knowledge_base" className="space-y-6">
                            <KnowledgeBaseAdmin />
                        </TabsContent>
                    )}

                    {/* AI Assistant Tab - Admin Full Only */}
                    {isAdminFull && (
                        <TabsContent value="ai_assistant" className="space-y-6">
                            <TenantAIConfigAdmin kbFocusDocId={kbDeepLinkDocId} />
                        </TabsContent>
                    )}

                    {/* Maintenance Tab - Admin Full Only */}
                    {isAdminFull && (
                        <TabsContent value="maintenance" className="space-y-6">
                            <FileVaultPanel />
                            {/* Demandes de suppression */}
                            <DeletionRequestsPanel />

                            {/* Corbeille des dossiers (restauration) */}
                            <ProspectsTrashPanel />
                            
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Wrench className="w-5 h-5" />
                                        Réparation des équipes dossiers
                                    </CardTitle>
                                    <CardDescription>
                                        Corrige les dossiers avec une équipe incorrecte en recalculant via le VP de l'apporteur
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    {/* Étape 1: Charger l'aperçu */}
                                    <div className="border rounded-lg p-4 space-y-4">
                                        <div className="flex items-start gap-3">
                                            <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                                            <div className="flex-1">
                                                <h4 className="font-medium text-slate-900">Étape 1 : Analyser les dossiers</h4>
                                                <p className="text-sm text-slate-600 mt-1">
                                                    Cliquez pour analyser tous les dossiers et voir lesquels ont une équipe incorrecte.
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <Button 
                                            onClick={handleLoadRepairPreview}
                                            disabled={loadingPreview}
                                            variant="outline"
                                            className="gap-2"
                                            data-testid="analyze-teams-button"
                                        >
                                            {loadingPreview ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                    Analyse en cours...
                                                </>
                                            ) : (
                                                <>
                                                    <RefreshCw className="w-4 h-4" />
                                                    Analyser les dossiers
                                                </>
                                            )}
                                        </Button>

                                        {/* Résultat de l'analyse */}
                                        {repairPreview && (
                                            <div className="mt-4 p-4 rounded-lg bg-slate-50 border border-slate-200 space-y-4">
                                                <h5 className="font-medium text-slate-900">Résultat de l'analyse</h5>
                                                
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                    <div className="bg-white p-3 rounded border">
                                                        <p className="text-2xl font-bold text-slate-900">{repairPreview.summary.total_prospects_with_apporteur}</p>
                                                        <p className="text-xs text-slate-500">Dossiers avec apporteur</p>
                                                    </div>
                                                    <div className="bg-white p-3 rounded border border-green-200">
                                                        <p className="text-2xl font-bold text-green-600">{repairPreview.summary.already_correct}</p>
                                                        <p className="text-xs text-slate-500">Déjà corrects</p>
                                                    </div>
                                                    <div className="bg-white p-3 rounded border border-amber-200">
                                                        <p className="text-2xl font-bold text-amber-600">{repairPreview.summary.auto_fixable}</p>
                                                        <p className="text-xs text-slate-500">À corriger (auto)</p>
                                                    </div>
                                                    <div className="bg-white p-3 rounded border border-red-200">
                                                        <p className="text-2xl font-bold text-red-600">{repairPreview.summary.manual_required}</p>
                                                        <p className="text-xs text-slate-500">Correction manuelle</p>
                                                    </div>
                                                </div>

                                                {/* Liste des corrections automatiques */}
                                                {repairPreview.auto_fixable_details?.length > 0 && (
                                                    <div className="mt-4">
                                                        <p className="text-sm font-medium text-slate-700 mb-2">
                                                            Dossiers à corriger automatiquement ({repairPreview.summary.auto_fixable}) :
                                                        </p>
                                                        <div className="max-h-48 overflow-y-auto bg-white rounded border p-2 space-y-1">
                                                            {repairPreview.auto_fixable_details.slice(0, 20).map((d, i) => (
                                                                <p key={i} className="text-xs text-slate-600">
                                                                    • <strong>{d.prospect_name}</strong> ({d.apporteur_name}) : 
                                                                    <span className="text-red-500"> {d.current_team_name || 'Aucune'}</span> → 
                                                                    <span className="text-green-500"> {d.correct_team_name}</span>
                                                                </p>
                                                            ))}
                                                            {repairPreview.auto_fixable_details.length > 20 && (
                                                                <p className="text-xs text-slate-400 italic">
                                                                    ... et {repairPreview.auto_fixable_details.length - 20} autres
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Liste des corrections manuelles */}
                                                {repairPreview.manual_required_details?.length > 0 && (
                                                    <div className="mt-4">
                                                        <p className="text-sm font-medium text-red-700 mb-2">
                                                            ⚠️ Dossiers nécessitant une correction manuelle ({repairPreview.summary.manual_required}) :
                                                        </p>
                                                        <div className="max-h-48 overflow-y-auto bg-red-50 rounded border border-red-200 p-2 space-y-1">
                                                            {repairPreview.manual_required_details.map((d, i) => (
                                                                <p key={i} className="text-xs text-red-700">
                                                                    • <strong>{d.prospect_name}</strong> - {d.reason}
                                                                </p>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Étape 2: Exécuter la correction */}
                                    {repairPreview && repairPreview.summary.auto_fixable > 0 && (
                                        <div className="border rounded-lg p-4 space-y-4 border-amber-200 bg-amber-50">
                                            <div className="flex items-start gap-3">
                                                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                                                <div className="flex-1">
                                                    <h4 className="font-medium text-amber-900">Étape 2 : Exécuter la correction</h4>
                                                    <p className="text-sm text-amber-700 mt-1">
                                                        <strong>{repairPreview.summary.auto_fixable}</strong> dossiers vont être corrigés. 
                                                        Cette action est irréversible mais un log d'audit sera conservé.
                                                    </p>
                                                </div>
                                            </div>
                                            
                                            <Button 
                                                onClick={handleExecuteRepair}
                                                disabled={executingRepair}
                                                variant="destructive"
                                                className="gap-2"
                                                data-testid="execute-repair-button"
                                            >
                                                {executingRepair ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                        Correction en cours...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Check className="w-4 h-4" />
                                                        Exécuter la correction ({repairPreview.summary.auto_fixable} dossiers)
                                                    </>
                                                )}
                                            </Button>

                                            {/* Résultat de l'exécution */}
                                            {repairResult && (
                                                <div className={`mt-4 p-4 rounded-lg ${repairResult.error ? 'bg-red-100 border border-red-300' : 'bg-green-100 border border-green-300'}`}>
                                                    {repairResult.error ? (
                                                        <p className="text-red-700 text-sm">Erreur: {repairResult.error}</p>
                                                    ) : (
                                                        <div className="space-y-2">
                                                            <p className="text-green-800 font-medium">
                                                                ✅ {repairResult.fixed_count} dossier(s) corrigé(s) avec succès
                                                            </p>
                                                            {repairResult.errors_count > 0 && (
                                                                <p className="text-amber-700 text-sm">
                                                                    ⚠️ {repairResult.errors_count} erreur(s) rencontrée(s)
                                                                </p>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Check Production Card */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Shield className="w-5 h-5 text-green-600" />
                                        Vérification Production
                                    </CardTitle>
                                    <CardDescription>
                                        Exécutez les vérifications avant le déploiement en production
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    <Button 
                                        onClick={handleRunProductionCheck}
                                        disabled={loadingProductionCheck}
                                        className="gap-2"
                                        data-testid="run-production-check-btn"
                                    >
                                        {loadingProductionCheck ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Vérification en cours...
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle className="w-4 h-4" />
                                                Lancer la vérification
                                            </>
                                        )}
                                    </Button>

                                    {productionCheckResult && (
                                        <div className="space-y-4 mt-4">
                                            {/* Summary */}
                                            <div className={`p-4 rounded-lg ${productionCheckResult.status === 'PASS' ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                                                <div className="flex items-center gap-3 mb-2">
                                                    {productionCheckResult.status === 'PASS' ? (
                                                        <CheckCircle className="w-6 h-6 text-green-600" />
                                                    ) : (
                                                        <AlertTriangle className="w-6 h-6 text-amber-600" />
                                                    )}
                                                    <span className="font-bold text-lg">
                                                        {productionCheckResult.status === 'PASS' ? 'Tous les tests passent !' : 'Attention requise'}
                                                    </span>
                                                </div>
                                                <div className="grid grid-cols-3 gap-4 mt-3">
                                                    <div className="text-center">
                                                        <p className="text-2xl font-bold text-slate-900">{productionCheckResult.summary.total_tests}</p>
                                                        <p className="text-xs text-slate-500">Total tests</p>
                                                    </div>
                                                    <div className="text-center">
                                                        <p className="text-2xl font-bold text-green-600">{productionCheckResult.summary.passed}</p>
                                                        <p className="text-xs text-slate-500">Passés</p>
                                                    </div>
                                                    <div className="text-center">
                                                        <p className="text-2xl font-bold text-red-600">{productionCheckResult.summary.failed}</p>
                                                        <p className="text-xs text-slate-500">Échoués</p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Check Details */}
                                            <div className="space-y-2">
                                                {productionCheckResult.checks?.map((check, idx) => (
                                                    <div key={idx} className="border rounded-lg p-3">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <h4 className="font-medium">{check.name}</h4>
                                                            <Badge variant={check.failed === 0 ? 'success' : 'destructive'}>
                                                                {check.passed}/{check.passed + check.failed}
                                                            </Badge>
                                                        </div>
                                                        <div className="space-y-1">
                                                            {check.tests?.map((test, tidx) => (
                                                                <div key={tidx} className="flex items-center gap-2 text-sm">
                                                                    {test.passed ? (
                                                                        <CheckCircle className="w-4 h-4 text-green-500" />
                                                                    ) : (
                                                                        <X className="w-4 h-4 text-red-500" />
                                                                    )}
                                                                    <span className={test.passed ? 'text-slate-600' : 'text-red-600'}>
                                                                        {test.name}: {test.details}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Cleanup Section */}
                                            {productionCheckResult.cleanup_items && (
                                                <div className="border-t pt-4 mt-4">
                                                    <h4 className="font-medium flex items-center gap-2 mb-3 text-amber-700">
                                                        <Trash2 className="w-4 h-4" />
                                                        Données de test à nettoyer
                                                    </h4>
                                                    
                                                    {productionCheckResult.cleanup_items.users?.length > 0 && (
                                                        <div className="mb-4">
                                                            <p className="text-sm font-medium mb-2">Utilisateurs de test ({productionCheckResult.cleanup_items.users.length})</p>
                                                            <div className="max-h-32 overflow-y-auto bg-slate-50 rounded p-2 space-y-1">
                                                                {productionCheckResult.cleanup_items.users.map(user => (
                                                                    <div key={user.id} className="flex items-center justify-between text-sm">
                                                                        <span>{user.email} ({user.first_name} {user.last_name})</span>
                                                                        <Button 
                                                                            variant="ghost" 
                                                                            size="sm"
                                                                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-6 px-2"
                                                                            onClick={() => handleDeleteTestUser(user.id, user.email)}
                                                                            data-testid={`delete-user-${user.id}`}
                                                                        >
                                                                            <Trash2 className="w-3 h-3" />
                                                                        </Button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {productionCheckResult.cleanup_items.prospects?.length > 0 && (
                                                        <div>
                                                            <p className="text-sm font-medium mb-2">Prospects de test ({productionCheckResult.cleanup_items.prospects.length})</p>
                                                            <div className="max-h-32 overflow-y-auto bg-slate-50 rounded p-2 space-y-1">
                                                                {productionCheckResult.cleanup_items.prospects.map(prospect => (
                                                                    <div key={prospect.id} className="flex items-center justify-between text-sm">
                                                                        <span>{prospect.company_name || `${prospect.first_name} ${prospect.last_name}`}</span>
                                                                        <Button 
                                                                            variant="ghost" 
                                                                            size="sm"
                                                                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-6 px-2"
                                                                            onClick={() => handleArchiveTestProspect(prospect.id, prospect.company_name)}
                                                                            data-testid={`archive-prospect-${prospect.id}`}
                                                                        >
                                                                            <Trash2 className="w-3 h-3" />
                                                                        </Button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Recalcul des commissions */}
                            <Card className="border-amber-200">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-amber-700">
                                        <RefreshCw className="w-5 h-5" />
                                        Recalcul des commissions
                                    </CardTitle>
                                    <CardDescription>
                                        Recalcule toutes les commissions des dossiers pour corriger les valeurs incorrectes en base.
                                        <span className="block mt-1 text-amber-600 font-medium">Utilisez uniquement si des incohérences sont détectées</span>
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <Button 
                                        onClick={async () => {
                                            if (!window.confirm('Êtes-vous sûr de vouloir recalculer TOUTES les commissions ? Cette opération peut prendre quelques minutes.')) return;
                                            setRecalculatingCommissions(true);
                                            try {
                                                const response = await api.post('/admin/recalculate-all-commissions');
                                                toast.success(`${response.data.updated_count} dossiers mis à jour`);
                                                if (response.data.errors_count > 0) {
                                                    toast.warning(`${response.data.errors_count} erreurs rencontrées`);
                                                }
                                            } catch (err) {
                                                toast.error(err.response?.data?.detail || 'Erreur lors du recalcul');
                                            } finally {
                                                setRecalculatingCommissions(false);
                                            }
                                        }}
                                        disabled={recalculatingCommissions}
                                        variant="outline"
                                        className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                                        data-testid="recalculate-commissions-btn"
                                    >
                                        {recalculatingCommissions ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Recalcul en cours...
                                            </>
                                        ) : (
                                            <>
                                                <RefreshCw className="w-4 h-4" />
                                                Recalculer toutes les commissions
                                            </>
                                        )}
                                    </Button>
                                </CardContent>
                            </Card>

                            {/* Audit des régressions d'étapes */}
                            <Card className="border-red-200">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-red-700">
                                        <AlertTriangle className="w-5 h-5" />
                                        Audit : Dossiers qui ont régressé
                                    </CardTitle>
                                    <CardDescription>
                                        Liste des dossiers qui sont passés de "Gagné" à une étape précédente.
                                        <span className="block mt-1 text-red-600 font-medium">Utile pour comprendre pourquoi le nombre de dossiers gagnés baisse</span>
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <Button 
                                        onClick={handleLoadStageReversals}
                                        disabled={loadingReversals}
                                        variant="outline"
                                        className="gap-2 border-red-300 text-red-700 hover:bg-red-50"
                                        data-testid="load-stage-reversals-btn"
                                    >
                                        {loadingReversals ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Chargement...
                                            </>
                                        ) : (
                                            <>
                                                <RefreshCw className="w-4 h-4" />
                                                Charger l'audit
                                            </>
                                        )}
                                    </Button>

                                    {stageReversals && (
                                        <div className="mt-4 space-y-4">
                                            {/* Résumé */}
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                <div className="bg-green-50 p-3 rounded border border-green-200">
                                                    <p className="text-2xl font-bold text-green-700">{stageReversals.current_won_count?.total || 0}</p>
                                                    <p className="text-xs text-green-600">Dossiers gagnés actuels</p>
                                                </div>
                                                <div className="bg-amber-50 p-3 rounded border border-amber-200">
                                                    <p className="text-2xl font-bold text-amber-700">{stageReversals.archived_won_count?.total || 0}</p>
                                                    <p className="text-xs text-amber-600">Gagnés mais archivés</p>
                                                </div>
                                                <div className="bg-red-50 p-3 rounded border border-red-200">
                                                    <p className="text-2xl font-bold text-red-700">{stageReversals.reversals_count || 0}</p>
                                                    <p className="text-xs text-red-600">Régressions détectées</p>
                                                </div>
                                                <div className="bg-blue-50 p-3 rounded border border-blue-200">
                                                    <p className="text-2xl font-bold text-blue-700">
                                                        {(stageReversals.current_won_count?.easy_power || 0)} / {(stageReversals.current_won_count?.chantier || 0)}
                                                    </p>
                                                    <p className="text-xs text-blue-600">Courtage / Travaux d’optimisation gagnés</p>
                                                </div>
                                            </div>

                                            {/* Liste des régressions */}
                                            {stageReversals.stage_reversals?.length > 0 ? (
                                                <div className="border rounded-lg overflow-hidden">
                                                    <Table>
                                                        <TableHeader>
                                                            <TableRow className="bg-red-50">
                                                                <TableHead className="font-semibold">Dossier</TableHead>
                                                                <TableHead className="font-semibold">Ancienne étape</TableHead>
                                                                <TableHead className="font-semibold">Nouvelle étape</TableHead>
                                                                <TableHead className="font-semibold">Modifié par</TableHead>
                                                                <TableHead className="font-semibold">Date</TableHead>
                                                                <TableHead className="font-semibold text-center">Actions</TableHead>
                                                            </TableRow>
                                                        </TableHeader>
                                                        <TableBody>
                                                            {stageReversals.stage_reversals.map((reversal, idx) => (
                                                                <TableRow key={idx} className="hover:bg-red-50/50">
                                                                    <TableCell className="font-medium">
                                                                        {reversal.prospect_name || reversal.prospect_id?.slice(0, 8)}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Badge className="bg-green-100 text-green-700">
                                                                            {getStageLabel(reversal.old_stage)}
                                                                        </Badge>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Badge className={getStageBadgeColor(reversal.new_stage)}>
                                                                            {getStageLabel(reversal.new_stage)}
                                                                        </Badge>
                                                                    </TableCell>
                                                                    <TableCell className="text-slate-600">
                                                                        {reversal.changed_by_name || 'Système'}
                                                                    </TableCell>
                                                                    <TableCell className="text-slate-500 text-sm">
                                                                        {formatDate(reversal.changed_at)}
                                                                    </TableCell>
                                                                    <TableCell className="text-center">
                                                                        <Button
                                                                            size="sm"
                                                                            variant="outline"
                                                                            className="border-green-300 text-green-700 hover:bg-green-50"
                                                                            onClick={async () => {
                                                                                try {
                                                                                    const pipelineType = reversal.old_stage?.startsWith('ch_') ? 'chantier' : 'easy_power';
                                                                                    await api.post(`/admin/audit/restore-to-won/${reversal.prospect_id}`, { pipeline_type: pipelineType });
                                                                                    toast.success(`${reversal.prospect_name} remis à l'étape Gagné`);
                                                                                    handleLoadStageReversals();
                                                                                } catch (error) {
                                                                                    toast.error(error.response?.data?.detail || 'Erreur lors de la restauration');
                                                                                }
                                                                            }}
                                                                        >
                                                                            <Undo2 className="w-4 h-4 mr-1" />
                                                                            Remettre Gagné
                                                                        </Button>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </div>
                                            ) : (
                                                <div className="text-center py-8 bg-green-50 rounded-lg border border-green-200">
                                                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                                                    <p className="text-green-700 font-medium">Aucune régression détectée</p>
                                                    <p className="text-green-600 text-sm">Tous les dossiers gagnés sont restés à cette étape</p>
                                                </div>
                                            )}

                                            {/* Liste des dossiers archivés qui étaient gagnés */}
                                            {stageReversals.archived_won_prospects?.length > 0 && (
                                                <div className="mt-6">
                                                    <h4 className="font-semibold text-amber-700 mb-3 flex items-center gap-2">
                                                        <ArchiveRestore className="w-5 h-5" />
                                                        Dossiers gagnés mais archivés ({stageReversals.archived_won_prospects.length})
                                                    </h4>
                                                    <div className="border rounded-lg overflow-hidden border-amber-200">
                                                        <Table>
                                                            <TableHeader>
                                                                <TableRow className="bg-amber-50">
                                                                    <TableHead className="font-semibold">Dossier</TableHead>
                                                                    <TableHead className="font-semibold">Pipeline</TableHead>
                                                                    <TableHead className="font-semibold">Étape</TableHead>
                                                                    <TableHead className="font-semibold">Archivé par</TableHead>
                                                                    <TableHead className="font-semibold">Date</TableHead>
                                                                    <TableHead className="font-semibold text-center">Actions</TableHead>
                                                                </TableRow>
                                                            </TableHeader>
                                                            <TableBody>
                                                                {stageReversals.archived_won_prospects.map((archived, idx) => (
                                                                    <TableRow key={idx} className="hover:bg-amber-50/50">
                                                                        <TableCell className="font-medium">
                                                                            {archived.prospect_name}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Badge variant="outline" className={archived.pipeline_type === 'easy_power' ? 'border-yellow-300 text-yellow-700' : 'border-orange-300 text-orange-700'}>
                                                                                {archived.pipeline_type === 'easy_power' ? 'Courtage' : 'Travaux d’optimisation'}
                                                                            </Badge>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Badge className="bg-green-100 text-green-700">
                                                                                {getStageLabel(archived.stage)}
                                                                            </Badge>
                                                                        </TableCell>
                                                                        <TableCell className="text-slate-600">
                                                                            {archived.archived_by_name || 'Inconnu'}
                                                                        </TableCell>
                                                                        <TableCell className="text-slate-500 text-sm">
                                                                            {formatDate(archived.archived_at)}
                                                                        </TableCell>
                                                                        <TableCell className="text-center">
                                                                            <Button
                                                                                size="sm"
                                                                                variant="outline"
                                                                                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                                                                                onClick={async () => {
                                                                                    try {
                                                                                        await api.post(`/admin/audit/unarchive-prospect/${archived.prospect_id}`);
                                                                                        toast.success(`${archived.prospect_name} désarchivé`);
                                                                                        handleLoadStageReversals();
                                                                                    } catch (error) {
                                                                                        toast.error(error.response?.data?.detail || 'Erreur lors du désarchivage');
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <ArchiveRestore className="w-4 h-4 mr-1" />
                                                                                Désarchiver
                                                                            </Button>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>
                    )}
                    {(isAdmin || isAdminFull) && (
                        <TabsContent value="hashtags" className="space-y-6">
                            <HashtagManager />
                        </TabsContent>
                    )}
                </Tabs>
            </div>
            
            {/* Dialog Payeur */}
            <Dialog open={showPayerDialog} onOpenChange={setShowPayerDialog}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingPayer ? 'Modifier le payeur' : 'Nouveau payeur'}</DialogTitle>
                        <DialogDescription>
                            {editingPayer ? 'Modifiez les informations du payeur' : 'Ajoutez un nouveau payeur personnalisé'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Nom affiché *</Label>
                                <Input value={newPayer.name} onChange={(e) => setNewPayer(prev => ({ ...prev, name: e.target.value }))} placeholder="Ex: Société du VP Dupont" />
                            </div>
                            <div>
                                <Label>Raison sociale *</Label>
                                <Input value={newPayer.company_name} onChange={(e) => setNewPayer(prev => ({ ...prev, company_name: e.target.value }))} placeholder="Ex: Dupont Consulting SAS" />
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>SIRET</Label>
                                <Input value={newPayer.siret} onChange={(e) => setNewPayer(prev => ({ ...prev, siret: e.target.value }))} />
                            </div>
                            <div>
                                <Label>SIREN</Label>
                                <Input value={newPayer.siren} onChange={(e) => setNewPayer(prev => ({ ...prev, siren: e.target.value }))} />
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>N° TVA Intracommunautaire</Label>
                                <Input value={newPayer.tva_number} onChange={(e) => setNewPayer(prev => ({ ...prev, tva_number: e.target.value }))} />
                            </div>
                            <div>
                                <Label>Email</Label>
                                <Input type="email" value={newPayer.email} onChange={(e) => setNewPayer(prev => ({ ...prev, email: e.target.value }))} />
                            </div>
                        </div>
                        
                        <div className="border-t pt-4">
                            <h4 className="font-medium mb-3">Adresse</h4>
                            <div className="space-y-3">
                                <Input value={newPayer.address} onChange={(e) => setNewPayer(prev => ({ ...prev, address: e.target.value }))} placeholder="Adresse" />
                                <div className="grid grid-cols-2 gap-4">
                                    <Input value={newPayer.postal_code} onChange={(e) => setNewPayer(prev => ({ ...prev, postal_code: e.target.value }))} placeholder="Code postal" />
                                    <Input value={newPayer.city} onChange={(e) => setNewPayer(prev => ({ ...prev, city: e.target.value }))} placeholder="Ville" />
                                </div>
                            </div>
                        </div>
                        
                        <div className="border-t pt-4">
                            <h4 className="font-medium mb-3">Coordonnées bancaires</h4>
                            <div className="grid grid-cols-3 gap-4">
                                <Input value={newPayer.iban} onChange={(e) => setNewPayer(prev => ({ ...prev, iban: e.target.value }))} placeholder="IBAN" />
                                <Input value={newPayer.bic} onChange={(e) => setNewPayer(prev => ({ ...prev, bic: e.target.value }))} placeholder="BIC" />
                                <Input value={newPayer.bank_name} onChange={(e) => setNewPayer(prev => ({ ...prev, bank_name: e.target.value }))} placeholder="Banque" />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowPayerDialog(false)}>Annuler</Button>
                        <Button onClick={handleCreatePayer} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
                            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                            {editingPayer ? 'Modifier' : 'Créer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Dialog Modification Utilisateur */}
            <EditUserDialog
                open={editUserDialog.open}
                onOpenChange={(open) => setEditUserDialog({ open, user: editUserDialog.user })}
                user={editUserDialog.user}
                vps={allUsers.filter(u => (u.role === 'vp' || u.is_vp_effective) && u.is_active !== false)}
                teams={teams}
                onSuccess={loadUsers}
            />

            {/* Modal fallback : lien d'invitation copiable après création d'un parrain */}
            <Dialog open={!!inviteInfo} onOpenChange={(o) => !o && setInviteInfo(null)}>
                <DialogContent className="max-w-lg" data-testid="new-user-invite-dialog">
                    <DialogHeader>
                        <DialogTitle>Invitation créée pour {inviteInfo?.name}</DialogTitle>
                        <DialogDescription>
                            Un email a été envoyé à <b>{inviteInfo?.email}</b>. Si le parrain ne le reçoit pas dans quelques minutes, transmettez-lui ce lien manuellement (SMS, WhatsApp…).
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                            <div className="text-xs text-slate-500 mb-1">Lien de création de mot de passe (valide 7 jours) :</div>
                            <div className="flex items-center gap-2">
                                <Input readOnly value={inviteInfo?.link || ''} className="text-xs font-mono" data-testid="new-user-invite-link-input" onFocus={(e) => e.target.select()} />
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => { navigator.clipboard?.writeText(inviteInfo?.link || ''); toast.success('Lien copié !'); }}
                                    data-testid="new-user-invite-link-copy"
                                >
                                    Copier
                                </Button>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={() => setInviteInfo(null)} data-testid="new-user-invite-link-close">Fermer</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}
